import React, { useState, useContext, useEffect, useRef } from 'react';
import './styles/Request.css';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { useNavigate, useParams } from 'react-router-dom';
import { UserContext } from '../UserContext';
import { notifyError, notifySuccess, ToastContainer } from '../components/ErrorAlert';
import { NotifyMessages } from '../components/NotifyMessages';
import { FaCloudUploadAlt, FaBookmark, FaRegBookmark, FaRegAddressBook } from 'react-icons/fa';
import { MdDelete } from "react-icons/md";
import { getAutocompleteSuggestions } from '../components/PlacesAutocomplete';
import useDebounce from '../components/UseDebounce';
import { Autocomplete, useJsApiLoader } from '@react-google-maps/api';
import { IoMdArrowBack } from "react-icons/io";
import { openSecureFileUrl } from '../utils/fileAccess';
import Select, { components } from 'react-select';
import { REQUEST_MESSAGES } from "../constants/requestMessages";
import useRequestUploads from "../hooks/useRequestUploads";
import FilePreviewModal from "../components/FilePreviewModal";

import {
  getSavedIndividualsStorageKey,
  createEmptyIndividual,
  calculateAgeLabel,
  getDobInputValue,
  isTravelSupport,
  getTodayInputDate,
  getCleanFileName,
  parseStoredFileValue,
  getFinalDeclarationText,
  getTravelClassOptions,
} from "../utils/requestHelpers";

import {
  getSavedIndividuals,
  getSavedAddresses,
  createSavedAddress,
  deleteSavedAddress,
} from "../services/requestService";

import { buildIndividualsToAppend } from "../utils/requestIndividuals";

const TRAVEL_PREFERENCE_OPTIONS = [
  { value: 'Bus', label: 'Via Bus' },
  { value: 'Train', label: 'Via Train' },
  { value: 'Flight', label: 'Via Flight' },
  { value: 'Own Vehicle', label: 'Own Vehicle' },
];

const TRAVEL_SUPPORT_TYPES = ['Travel', 'Accommodation', 'Travel + Accommodation'];
const TRAVEL_SUPPORT_WITH_TICKET = ['Travel', 'Travel + Accommodation'];
const ACCOMMODATION_SUPPORT_TYPES = ['Accommodation', 'Travel + Accommodation'];

// Case/whitespace-insensitive location comparison, used to block matching Source/Destination pairs.
const normalizeLocation = (value) => (value || '').trim().toLowerCase();
const isSameLocation = (a, b) =>
  Boolean(a) && Boolean(b) && normalizeLocation(a) === normalizeLocation(b);

const SavedIndividualOption = (props) => {
  const { data, isSelected } = props;
  const savedIndividual = data.data;
  return (
    <components.Option {...props}>
      <div className="saved-individual-option">
        <input type="checkbox" checked={isSelected} readOnly />
        <div>
          <div className="saved-individual-name">{savedIndividual.name}</div>
          <div className="saved-individual-meta">
            {[savedIndividual.gender, getDobInputValue(savedIndividual.dob), savedIndividual.idNumber, savedIndividual.contactNumber]
              .filter(Boolean)
              .join(' | ')}
          </div>
        </div>
      </div>
    </components.Option>
  );
};

function Request() {
  const { user, token } = useContext(UserContext);
  const navigate = useNavigate();
  const { requestId } = useParams();

  // --- Form data & validation state ---
  const [errors, setErrors] = useState({});
  const [isFormValid, setIsFormValid] = useState(false);
  const [digitError, setDigitError] = useState({});
  const [formData, setFormData] = useState({
    supportNeeded: '',
    individuals: [],
    source: '',
    destination: '',
    travelPreference: '',
    travelClass: '',
    trainNumber: '',
    trainTiming: '',
    busName: '',
    busTiming: '',
    fromDate: '',
    toDate: '',

    // Trip Type
    tripType: 'One Way',

    // Return Journey
    returnSource: '',
    returnDestination: '',
    returnTravelPreference: '',
    returnTravelClass: '',
    returnTrainNumber: '',
    returnTrainTiming: '',
    returnBusName: '',
    returnBusTiming: '',
    returnDate: '',
    returnRemarks: '',
    
    batchId: '',
    center: '',
    client: '',
    costCenter: '',
    purpose: '',
    otherPurpose: '',
    otherCostCenter: '',
    email: user ? user.email : '',
    userId: user ? user.id : null,
    courierType: '',
    otherCourierType: '',
    officeLocation: user ? user.officeLocation || '' : '',
    fromLocation: '',
    toLocation: '',
    sentFrom: '',
    receivedBy: '',
    receiverEmail: '',
    receivedOn: '',
    podNumber: '',
    courierCo: '',
    requesterRemarks: '',
    selfBooked: false,
    selfBookedTicketAmount: '',
    selfBookedTicketBill: null,
    selfBookedHotelDetails: '',
    selfBookedHotelAmount: '',
    selfBookedHotelBill: null,
    paymentMode: '',
    otherPaymentMode: '',
    paymentReference: '',
    paymentUpiNumber: '',
    paymentDate: '',
    paymentProof: null,
    reimbursementPaymentType: '',
    reimbursementUpiId: '',
    reimbursementUpiNumber: '',
    reimbursementAccountNumber: '',
    reimbursementAccountHolderName: '',
    reimbursementSupportingDocument: null,
    ticketDeclarationAccepted: false,
    accommodationDeclarationAccepted: false,
    paymentDeclarationAccepted: false,
    finalDeclarationAccepted: false,
  });
  const manualDateEditingRef = useRef({
    fromDate: false,
    toDate: false,
  });

  // --- Autocomplete suggestions (source/destination address typeahead) ---
  const [sourceSuggestions, setSourceSuggestions] = useState([]);
  const [destinationSuggestions, setDestinationSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);

  // --- Self-booking file upload state (ticket, hotel, payment proof) ---
  const [dragTarget, setDragTarget] = useState(null);
  const [selfTicketBillFile, setSelfTicketBillFile] = useState(null);
  const [selfTicketBillName, setSelfTicketBillName] = useState('');
  const [selfTicketBillUrl, setSelfTicketBillUrl] = useState('');
  const [selfHotelBillFile, setSelfHotelBillFile] = useState(null);
  const [selfHotelBillName, setSelfHotelBillName] = useState('');
  const [selfHotelBillUrl, setSelfHotelBillUrl] = useState('');
  const [selfPaymentProofFile, setSelfPaymentProofFile] = useState(null);
  const [selfPaymentProofName, setSelfPaymentProofName] = useState('');
  const [selfPaymentProofUrl, setSelfPaymentProofUrl] = useState('');
  
  // --- Reimbursement supporting document upload state (tracked like the other self-booking files) ---
  const [selfReimbursementDocFile, setSelfReimbursementDocFile] = useState(null);
  const [selfReimbursementDocName, setSelfReimbursementDocName] = useState('');
  const [selfReimbursementDocUrl, setSelfReimbursementDocUrl] = useState('');

  // --- Saved individuals state ---
  const [savedIndividuals, setSavedIndividuals] = useState([]);
  const [savedIndividualsLoading, setSavedIndividualsLoading] = useState(false);
  const [selectedSavedIndividuals, setSelectedSavedIndividuals] = useState([]);
  const savedIndividualsSelectRef = useRef(null);
  const [savingInformation, setSavingInformation] = useState(false);

  // --- Saved addresses state ---
  const originAutocompleteRef = useRef(null);
  const destinationAutocompleteRef = useRef(null);
  const returnOriginAutocompleteRef = useRef(null);
  const returnDestinationAutocompleteRef = useRef(null);
  const [savedAddresses, setSavedAddresses] = useState([]);
  const [savedAddressSearch, setSavedAddressSearch] = useState("");
  const [showSavedAddressPopup, setShowSavedAddressPopup] = useState(false);
  const [activeAddressField, setActiveAddressField] = useState("");
  const [loadingSavedAddresses, setLoadingSavedAddresses] = useState(false);
  const [savedAddressStatus, setSavedAddressStatus] = useState({
    source: false,
    destination: false,
    returnSource: false,
    returnDestination: false,
  });

  // --- Document preview modal state ---
  const [showDocPreviewModal, setShowDocPreviewModal] = useState(false);
  const [docDownloadFile, setDocDownloadFile] = useState(null);

  const dispatchInboxRefresh = () => {
    window.dispatchEvent(new Event('routeplanner:inbox-refresh'));
  };

  // --- Google Places "onPlaceChanged" handlers ---
  function handleOriginPlaceChanged() {
    const place = originAutocompleteRef.current?.getPlace();
    const addr = place?.formatted_address ?? '';

    if (isSameLocation(addr, formData.destination)) {
      notifyError(REQUEST_MESSAGES.SOURCE_DESTINATION_SAME);
      setFormData(prev => ({ ...prev, source: "" }));
      return;
    }

    if (addr) {
      setFormData(prev => ({ ...prev, source: addr }));
      clearErrorFor('source');
    }
  }

  function handleDestinationPlaceChanged() {
    const place = destinationAutocompleteRef.current?.getPlace();
    const addr = place?.formatted_address ?? '';

    if (isSameLocation(addr, formData.source)) {
      notifyError(REQUEST_MESSAGES.SOURCE_DESTINATION_SAME);
      setFormData(prev => ({ ...prev, destination: "" }));
      return;
    }

    if (addr) {
      setFormData(prev => ({ ...prev, destination: addr }));
      clearErrorFor('destination');
    }
  }

  function handleReturnOriginPlaceChanged() {
    const place = returnOriginAutocompleteRef.current?.getPlace();
    const addr = place?.formatted_address ?? '';

    if (isSameLocation(addr, formData.returnDestination)) {
      notifyError(REQUEST_MESSAGES.RETURN_SOURCE_DESTINATION_SAME);
      setFormData(prev => ({ ...prev, returnSource: "" }));
      return;
    }

    if (addr) {
      setFormData(prev => ({ ...prev, returnSource: addr }));
      clearErrorFor('returnSource');
    }
  }

  function handleReturnDestinationPlaceChanged() {
    const place = returnDestinationAutocompleteRef.current?.getPlace();
    const addr = place?.formatted_address ?? '';

    if (isSameLocation(addr, formData.returnSource)) {
      notifyError(REQUEST_MESSAGES.RETURN_SOURCE_DESTINATION_SAME);
      setFormData(prev => ({ ...prev, returnDestination: "" }));
      return;
    }

    if (addr) {
      setFormData(prev => ({ ...prev, returnDestination: addr }));
      clearErrorFor('returnDestination');
    }
  }

const { isLoaded } = useJsApiLoader({
  id: 'script-loader',
  googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
  libraries: ['places'],
  region: "IN",
  language: "en",
});

const fetchSavedIndividuals = async () => {
  const storageKey = getSavedIndividualsStorageKey(user?.email);
  const cachedSavedIndividuals = (() => {
    try {
      return JSON.parse(localStorage.getItem(storageKey) || '[]');
    } catch (error) {
      return [];
    }
  })();

  if (cachedSavedIndividuals.length) {
    setSavedIndividuals(cachedSavedIndividuals);
  }

  if (!token) return;

  try {
    setSavedIndividualsLoading(true);
    const data = await getSavedIndividuals(token);
    setSavedIndividuals(Array.isArray(data) ? data : []);
    localStorage.setItem(storageKey, JSON.stringify(Array.isArray(data) ? data : []));
  } catch (error) {
    console.error('Error fetching saved individuals:', error);
    setSavedIndividuals(cachedSavedIndividuals);
  } finally {
    setSavedIndividualsLoading(false);
  }
};

const fetchSavedAddresses = async (search = "") => {
  if (!token) return;

  try {
    setLoadingSavedAddresses(true);
    const data = await getSavedAddresses(token, search);
    setSavedAddresses(Array.isArray(data) ? data : []);
  } catch (err) {
    console.error("Failed to fetch saved addresses:", err);
  } finally {
    setLoadingSavedAddresses(false);
  }
};

const isAddressSaved = (address) => {
  if (!address?.trim()) return false;
  return savedAddresses.some((item) => isSameLocation(item.address, address));
};

const handleSavedAddressSelect = (address) => {
  const field = activeAddressField;

  if (field === "source" && isSameLocation(address, formData.destination)) {
    notifyError(REQUEST_MESSAGES.SOURCE_DESTINATION_SAME, "bottom-center");
    return;
  }

  if (field === "destination" && isSameLocation(address, formData.source)) {
    notifyError(REQUEST_MESSAGES.SOURCE_DESTINATION_SAME, "bottom-center");
    return;
  }

  if (field === "returnSource" && isSameLocation(address, formData.returnDestination)) {
    notifyError(REQUEST_MESSAGES.RETURN_SOURCE_DESTINATION_SAME, "bottom-center");
    return;
  }

  if (field === "returnDestination" && isSameLocation(address, formData.returnSource)) {
    notifyError(REQUEST_MESSAGES.RETURN_SOURCE_DESTINATION_SAME, "bottom-center");
    return;
  }

  setFormData((prev) => ({
    ...prev,
    [field]: address,
  }));

  clearErrorFor(field);
  closeSavedAddressPopup();
};

const openSavedAddressPopup = async (field) => {
  setActiveAddressField(field);
  setSavedAddressSearch("");

  await fetchSavedAddresses();

  setShowSavedAddressPopup(true);
};

const closeSavedAddressPopup = () => {
  setShowSavedAddressPopup(false);
  setActiveAddressField("");
  setSavedAddressSearch("");
};

const saveAddress = async (field) => {
  const address = formData[field];

  if (!address?.trim()) {
    notifyError(REQUEST_MESSAGES.ADDRESS_ENTER_FIRST);
    return;
  }

  try {
    const data = await createSavedAddress(token, address);
    setSavedAddressStatus(prev => ({
      ...prev,
      [field]: true,
    }));

    notifySuccess(data.message);

    fetchSavedAddresses();

  } catch (err) {
    console.error(err);
    notifyError(err.message);
  }
};

const removeSavedAddress = async (field) => {
  const address = formData[field];

  const savedAddress = savedAddresses.find(
    item =>
      item.address.trim().toLowerCase() ===
      address.trim().toLowerCase()
  );

  if (!savedAddress) {
    notifyError(REQUEST_MESSAGES.ADDRESS_NOT_FOUND);
    return;
  }

  try {
    const data = await deleteSavedAddress(token, savedAddress.id);
    setSavedAddressStatus(prev => ({
      ...prev,
      [field]: false,
    }));

    notifySuccess(data.message);

    fetchSavedAddresses();

  } catch (err) {
    console.error(err);
    notifyError(err.message);
  }
};

const addSelectedSavedIndividualsToForm = (selectedItems = []) => {
  setFormData((prev) => {
    const individuals = buildIndividualsToAppend(
      prev.individuals,
      selectedItems
    );

    if (prev.purpose === "Assessment" && prev.individuals.length > 0) {
      const project = prev.individuals[0].project || "";
      const vertical = prev.individuals[0].vertical || "";

      return {
        ...prev,
        individuals: individuals.map((individual) => ({
          ...individual,
          project,
          vertical,
        })),
      };
    }

    return {
      ...prev,
      individuals,
    };
  });
};

const handleSecureLinkOpen = async (event, fileValue, fileName) => {
  event.preventDefault();

  const extension = fileName?.split(".").pop()?.toLowerCase();

  if (extension === "doc" || extension === "docx") {
    setDocDownloadFile(fileValue);
    setShowDocPreviewModal(true);
    return;
  }

  try {
    await openSecureFileUrl(fileValue, token);
  } catch (error) {
    console.error("Failed to open secure file:", error);
  }
};

const uploadSelfBookingFile = async (file, type, label) => {
  const payload = new FormData();
  payload.append('file', file);

  const response = await fetch(
    `${process.env.REACT_APP_API_URL}/requests/self-booking/upload/${requestId}/${type}`,
    {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
      },
      body: payload,
    }
  );

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`${label} upload failed: ${response.status} - ${errorText}`);
  }
};

  const isReimbursementJourney = formData.selfBooked;
  const todayInputDate = getTodayInputDate();
  const journeyDateMin = isReimbursementJourney ? undefined : todayInputDate;
  const journeyDateMax = isReimbursementJourney ? todayInputDate : undefined;

  const isJourneyDateAllowed = (dateValue, isSelfBooked) => {
    if (!dateValue) return true;
    return isSelfBooked ? dateValue <= todayInputDate : dateValue >= todayInputDate;
  };

  const debouncedInput = useDebounce(formData.source, 250);

  useEffect(() => {
    fetchSavedIndividuals();
    fetchSavedAddresses();
  }, [token]);

  useEffect(() => {
    setSavedAddressStatus({
      source: isAddressSaved(formData.source),
      destination: isAddressSaved(formData.destination),
      returnSource: isAddressSaved(formData.returnSource),
      returnDestination: isAddressSaved(formData.returnDestination),
    });
  }, [
    savedAddresses,
    formData.source,
    formData.destination,
    formData.returnSource,
    formData.returnDestination,
  ]);

  useEffect(() => {
    const pendingSavedIndividuals = sessionStorage.getItem('routeplanner-pending-saved-individuals');
    if (pendingSavedIndividuals) {
      try {
        addSelectedSavedIndividualsToForm(JSON.parse(pendingSavedIndividuals));
      } catch (error) {
        console.error('Failed to restore pending saved individuals:', error);
      } finally {
        sessionStorage.removeItem('routeplanner-pending-saved-individuals');
      }
    }
    const handleSavedIndividualsEvent = (event) => {
      addSelectedSavedIndividualsToForm(event.detail?.individuals || []);
    };

    const handleAddEmptyIndividualEvent = () => {
      addIndividual();
    };

    const handleSavedIndividualsUpdated = (event) => {
      const updatedIndividuals = event.detail?.savedIndividuals;

      if (Array.isArray(updatedIndividuals)) {
        setSavedIndividuals(updatedIndividuals);
      }
    };

    window.addEventListener('routeplanner:add-saved-individuals', handleSavedIndividualsEvent);
    window.addEventListener('routeplanner:add-empty-individual', handleAddEmptyIndividualEvent);
    window.addEventListener('routeplanner:saved-individuals-updated', handleSavedIndividualsUpdated);

    return () => {
      window.removeEventListener('routeplanner:add-saved-individuals', handleSavedIndividualsEvent);
      window.removeEventListener('routeplanner:add-empty-individual', handleAddEmptyIndividualEvent);
      window.removeEventListener('routeplanner:saved-individuals-updated', handleSavedIndividualsUpdated);
    };
  }, []);

   useEffect(() => {
   if (debouncedInput.trim()) {
       const fetchSuggestions = async () => {
       try {
           const suggestions = await getAutocompleteSuggestions(debouncedInput);
          setSourceSuggestions(suggestions);
        } catch (error) {
          console.error('Error fetching suggestions:', error);
        }
       };

     fetchSuggestions();
    } else {
      setSourceSuggestions([]); // Clear suggestions if input is empty
      setShowSuggestions((prev) => ({ ...prev, source: false }));
    }
  }, [debouncedInput]); // Dependency array ensures this runs when debouncedInput changes

  // Use the custom debounce hook
  const debouncedDestination = useDebounce(formData.destination, 500); // Debounce delay of 1 second

  // Fetch suggestions when the debounced input value changes
   useEffect(() => {
       if (debouncedDestination.trim()) {
       const fetchSuggestions = async () => {
         try {
           const suggestions = await getAutocompleteSuggestions(debouncedDestination);
           setDestinationSuggestions(suggestions);
         } catch (error) {
           console.error('Error fetching suggestions:', error);
         }
       };

    fetchSuggestions();
    } else {
    setDestinationSuggestions([]); // Clear suggestions if input is empty
     setShowSuggestions((prev) => ({ ...prev, destination: false }));
   }
   }, [debouncedDestination]); // Dependency array ensures this runs when debouncedInput changes

  // Effect to fetch request data if editing an existing request

useEffect(() => {
  const fetchData = async () => {
    try {
      if (!requestId) return;

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/requests/${requestId}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error(REQUEST_MESSAGES.FETCH_REQUEST_FAILED);
      }

      const data = await response.json();
      const bookingDetails = data.bookingDetails || {};

     setFormData(prev => ({
  ...prev,
  supportNeeded: data.supportNeeded,

  source: data.source || data.fromLocation || '',
  destination: data.destination || data.toLocation || '',
  travelPreference: data.travelPreference || '',
  travelClass: data.travelClass || '',
  trainNumber: data.trainNumber || '',
  trainTiming: data.trainTiming || '',
  busName: data.busName || '',
  busTiming: data.busTiming || '',
  fromDate: data.fromDate ? data.fromDate.split('T')[0] : '',
  toDate: data.toDate ? data.toDate.split('T')[0] : '',
  batchId: data.batchId || '',
  center: data.center || '',
  client: data.client || '',
  costCenter:
    [
      'Awarding Bodies',
      'Corporate Backed Foundations (CBF)',
      'Educational & Skilling Institutions (ESI)',
      'Platform Services',
    ].includes(data.costCenter) || !data.costCenter
      ? data.costCenter || ''
      : 'Others',
  otherCostCenter:
    data.costCenter &&
    ![
      'Awarding Bodies',
      'Corporate Backed Foundations (CBF)',
      'Educational & Skilling Institutions (ESI)',
      'Platform Services',
    ].includes(data.costCenter)
      ? data.costCenter
      : '',
  purpose:
    data.purpose !== 'Assessment' && data.purpose !== 'Meeting'
      ? 'Others'
      : data.purpose || '',
  otherPurpose:
    data.purpose !== 'Assessment' && data.purpose !== 'Meeting'
      ? data.purpose || ''
      : '',
  requesterRemarks: data.requesterRemarks || '',
  tripType: data.tripType || 'One Way',

returnSource: data.returnSource || '',
returnDestination: data.returnDestination || '',

returnTravelPreference: data.returnTravelPreference || '',
returnTravelClass: data.returnTravelClass || '',

returnTrainNumber: data.returnTrainNumber || '',
returnTrainTiming: data.returnTrainTiming || '',

returnBusName: data.returnBusName || '',
returnBusTiming: data.returnBusTiming || '',

returnDate: data.returnDate
  ? data.returnDate.split('T')[0]
  : '',

returnRemarks: data.returnRemarks || '',
  courierType: data.courierType || '',
  otherCourierType: data.otherCourierType || '',
  sentFrom: data.sentFrom || '',
  receivedBy: data.receivedBy || '',
  receiverEmail: data.receiverEmail || '',
  receivedOn: data.receivedOn ? data.receivedOn.split("T")[0] : '',
  podNumber: data.podNumber || '',
  courierCo: data.courierCo || '',
  selfBooked: Boolean(bookingDetails.selfBooked),
  selfBookedTicketAmount: bookingDetails.selfBookedTicketAmount || '',
  selfBookedTicketBill: bookingDetails.selfBookedTicketBill || null,
  selfBookedHotelDetails: bookingDetails.selfBookedHotelDetails || '',
  selfBookedHotelAmount: bookingDetails.selfBookedHotelAmount || '',
  selfBookedHotelBill: bookingDetails.selfBookedHotelBill || null,
  paymentMode: bookingDetails.paymentMode || '',
  paymentReference: bookingDetails.paymentReference || '',
  paymentUpiNumber: bookingDetails.paymentUpiNumber || '',
  paymentDate: bookingDetails.paymentDate
    ? bookingDetails.paymentDate.split('T')[0]
    : '',
  paymentProof: bookingDetails.paymentProof || null,
  reimbursementPaymentType: bookingDetails.reimbursementPaymentType || '',
  reimbursementUpiId: bookingDetails.reimbursementUpiId || '',
  reimbursementUpiNumber: bookingDetails.reimbursementUpiNumber || '',
  reimbursementAccountNumber: bookingDetails.reimbursementAccountNumber || '',
  reimbursementAccountHolderName: bookingDetails.reimbursementAccountHolderName || '',
  reimbursementSupportingDocument: bookingDetails.reimbursementSupportingDocument || null,
  ticketDeclarationAccepted: false,
  accommodationDeclarationAccepted: false,
  paymentDeclarationAccepted: false,
  finalDeclarationAccepted: false,
  individuals: data.individuals?.map(ind => {
    const parsedIdProof = parseStoredFileValue(ind.idProof || '');

    return {
      savedInfoId: null,
      name: ind.name || '',
      type: ind.type || '',
      gender: ind.gender || '',
      dob: ind.dob ? ind.dob.split('T')[0] : '',
      age: ind.ageDisplay || calculateAgeLabel(ind.dob),
      idType: ind.idType || '',
      idNumber: ind.idNumber || '',
      contactNumber: ind.contactNumber || '',
      project: ind.project || '',
      vertical: ind.vertical || '',
      idProofUrl: parsedIdProof.url,
      existingIdProof: parsedIdProof.raw || null,
      idProof: null,
    };
  }) || [createEmptyIndividual()],
}));

      const parsedTicketBill = parseStoredFileValue(bookingDetails.selfBookedTicketBill);
      setSelfTicketBillName(parsedTicketBill.name);
      setSelfTicketBillUrl(parsedTicketBill.url);

      const parsedHotelBill = parseStoredFileValue(bookingDetails.selfBookedHotelBill);
      setSelfHotelBillName(parsedHotelBill.name);
      setSelfHotelBillUrl(parsedHotelBill.url);

      const parsedPaymentProof = parseStoredFileValue(bookingDetails.paymentProof);
      setSelfPaymentProofName(parsedPaymentProof.name);
      setSelfPaymentProofUrl(parsedPaymentProof.url);

      const parsedReimbursementDoc = parseStoredFileValue(bookingDetails.reimbursementSupportingDocument);
      setSelfReimbursementDocName(parsedReimbursementDoc.name);
      setSelfReimbursementDocUrl(parsedReimbursementDoc.url);
    } catch (error) {
      console.error('Error fetching request data:', error);
    }
  };
  

  fetchData();
}, [requestId, token]);

  // // Handle suggestion click
  const handleSuggestionClick = (type, suggestion) => {
    if(type==='source'){
      setFormData((prevData) => ({ ...prevData, source: suggestion.description }));
     setShowSuggestions((prev) => ({ ...prev, source: false })); // Hide suggestions after selection
   }else {
    setFormData((prevData) => ({ ...prevData, destination: suggestion.description }));
    setShowSuggestions((prev) => ({ ...prev, destination: false })); // Hide suggestions after selection
   }
   };

  const handleDatePointerDown = (name) => {
    // Clicking the native calendar should be treated as calendar selection.
    manualDateEditingRef.current[name] = false;
  };

  const handleDateKeyDown = (e, name) => {
    // Keyboard editing should not trigger date validation while typing.
    if (
      e.key >= '0' && e.key <= '9' ||
      e.key === 'Backspace' ||
      e.key === 'Delete' ||
      e.key === 'ArrowLeft' ||
      e.key === 'ArrowRight'
    ) {
      manualDateEditingRef.current[name] = true;
    }
  };

  const handleDateBlur = (name) => {
    // Do not validate here. Final validation happens on submit.
    manualDateEditingRef.current[name] = false;
  };

  // Handle input changes
  const handleInputChange = async (e, index = null) => {
    const { name, value } = e.target;

     if (index === null) {
    clearErrorFor(name);
  } else {
    clearErrorFor(`individual_${index}_${name}`);
  }

if (name === 'supportNeeded') {
  const isTravelSupportType = TRAVEL_SUPPORT_WITH_TICKET.includes(value);
  const isAccommodationSupportType = ACCOMMODATION_SUPPORT_TYPES.includes(value);
  const isCourierSupportType = value.includes('Courier');

  setFormData(prev => ({
    ...prev,
    supportNeeded: value,

    ...(isTravelSupportType
      ? {
          courierType: '',
          otherCourierType: '',
          sentFrom: '',
          receivedBy: '',
          receiverEmail: '',
          receivedOn: '',
          podNumber: '',
          courierCo: '',
          ...(value === 'Travel'
            ? {
                toDate: '',
                selfBookedHotelDetails: '',
                selfBookedHotelAmount: '',
                selfBookedHotelBill: null,
              }
            : {}),
        }
      : isCourierSupportType
        ? {
            individuals: [],
            batchId: '',
            center: '',
            client: '',
            costCenter: '',
            otherCostCenter: '',
            purpose: '',
            otherPurpose: '',
            tripType: 'One Way',
            travelPreference: '',
            travelClass: '',
            trainNumber: '',
            trainTiming: '',
            busName: '',
            busTiming: '',
            fromDate: '',
            toDate: '',
            returnSource: '',
            returnDestination: '',
            returnTravelPreference: '',
            returnTravelClass: '',
            returnTrainNumber: '',
            returnTrainTiming: '',
            returnBusName: '',
            returnBusTiming: '',
            returnDate: '',
            returnRemarks: '',
            selfBooked: false,
            selfBookedTicketAmount: '',
            selfBookedTicketBill: null,
            selfBookedHotelDetails: '',
            selfBookedHotelAmount: '',
            selfBookedHotelBill: null,
            paymentMode: '',
            otherPaymentMode: '',
            paymentReference: '',
            paymentUpiNumber: '',
            paymentDate: '',
            paymentProof: null,
            reimbursementPaymentType: '',
            reimbursementUpiId: '',
            reimbursementUpiNumber: '',
            reimbursementAccountNumber: '',
            reimbursementAccountHolderName: '',
            reimbursementSupportingDocument: null,
            ticketDeclarationAccepted: false,
            accommodationDeclarationAccepted: false,
            paymentDeclarationAccepted: false,
            finalDeclarationAccepted: false,
          }
        : isAccommodationSupportType
          ? {
              courierType: '',
              otherCourierType: '',
              sentFrom: '',
              receivedBy: '',
              receiverEmail: '',
              receivedOn: '',
              podNumber: '',
              courierCo: '',
              source: '',
              tripType: 'One Way',
              travelPreference: '',
              travelClass: '',
              trainNumber: '',
              trainTiming: '',
              busName: '',
              busTiming: '',
              returnSource: '',
              returnDestination: '',
              returnTravelPreference: '',
              returnTravelClass: '',
              returnTrainNumber: '',
              returnTrainTiming: '',
              returnBusName: '',
              returnBusTiming: '',
              returnDate: '',
              returnRemarks: '',
              selfBookedTicketAmount: '',
              selfBookedTicketBill: null,
            }
        : {
            individuals: [],
            batchId: '',
            center: '',
            client: '',
            costCenter: '',
            otherCostCenter: '',
            purpose: '',
            otherPurpose: '',
            source: '',
            tripType: 'One Way',
            travelPreference: '',
            travelClass: '',
            trainNumber: '',
            trainTiming: '',
            busName: '',
            busTiming: '',
            fromDate: '',
            toDate: '',
            returnSource: '',
            returnDestination: '',
            returnTravelPreference: '',
            returnTravelClass: '',
            returnTrainNumber: '',
            returnTrainTiming: '',
            returnBusName: '',
            returnBusTiming: '',
            returnDate: '',
            returnRemarks: '',
            selfBooked: false,
            selfBookedTicketAmount: '',
            selfBookedTicketBill: null,
            selfBookedHotelDetails: '',
            selfBookedHotelAmount: '',
            selfBookedHotelBill: null,
            paymentMode: '',
            paymentReference: '',
            paymentUpiNumber: '',
            paymentDate: '',
            paymentProof: null,
            reimbursementPaymentType: '',
            reimbursementUpiId: '',
            reimbursementUpiNumber: '',
            reimbursementAccountNumber: '',
            reimbursementAccountHolderName: '',
            reimbursementSupportingDocument: null,
            ticketDeclarationAccepted: false,
            accommodationDeclarationAccepted: false,
            paymentDeclarationAccepted: false,
            finalDeclarationAccepted: false,
          }),
  }));

  if (!isTravelSupportType) {
    setSelfTicketBillFile(null);
    setSelfTicketBillName('');
    setSelfTicketBillUrl('');
  }
  if (!isAccommodationSupportType) {
    setSelfHotelBillFile(null);
    setSelfHotelBillName('');
    setSelfHotelBillUrl('');
  }
  if (!isTravelSupportType && !isAccommodationSupportType) {
    setSelfPaymentProofFile(null);
    setSelfPaymentProofName('');
    setSelfPaymentProofUrl('');
    setSelfReimbursementDocFile(null);
    setSelfReimbursementDocName('');
    setSelfReimbursementDocUrl('');
  }
  setErrors({});
  return;
}
if (name === 'travelPreference') {
  setFormData(prev => ({
    ...prev,
    travelPreference: value,
    travelClass: '',
    trainNumber: value === 'Train' ? prev.trainNumber : '',
    trainTiming: value === 'Train' ? prev.trainTiming : '',
    busName: value === 'Bus' ? prev.busName : '',
    busTiming: value === 'Bus' ? prev.busTiming : '',
  }));
  return;
}

if (name === 'returnTravelPreference') {
  setFormData(prev => ({
    ...prev,
    returnTravelPreference: value,
    returnTravelClass: '',
    returnTrainNumber: value === 'Train' ? prev.returnTrainNumber : '',
    returnTrainTiming: value === 'Train' ? prev.returnTrainTiming : '',
    returnBusName: value === 'Bus' ? prev.returnBusName : '',
    returnBusTiming: value === 'Bus' ? prev.returnBusTiming : '',
  }));
  return;
}
if (name === 'tripType' && value === 'One Way') {
  setFormData(prev => ({
    ...prev,
    tripType: value,
    returnSource: '',
    returnDestination: '',
    returnTravelPreference: '',
    returnTravelClass: '',
    returnTrainNumber: '',
    returnTrainTiming: '',
    returnBusName: '',
    returnBusTiming: '',
    returnDate: '',
    returnRemarks: '',
  }));

  setErrors(prev =>
    Object.fromEntries(
      Object.entries(prev).filter(([key]) => !key.startsWith('return'))
    )
  );
  return;
}
  if (name === "paymentMode") {
    setFormData((prev) => ({
      ...prev,
      paymentMode: value,
      otherPaymentMode: value === "Other" ? prev.otherPaymentMode : "",
      ...(value === "Cash"
        ? {
            paymentReference: "",
            paymentUpiNumber: "",
          }
        : {}),
    }));

    if (value === "Cash") {
      clearErrorFor("paymentReference");
      clearErrorFor("paymentUpiNumber");
    }
    if (value !== "Other") {
      clearErrorFor("otherPaymentMode");
    }

    return;
  }
  if (name === "costCenter") {
    setFormData((prev) => ({
      ...prev,
      costCenter: value,
      otherCostCenter: value === "Others" ? prev.otherCostCenter : "",
    }));
    clearErrorFor("otherCostCenter");
    return;
  }
  if (name === "purpose") {
    setFormData((prev) => {
      let individuals = [...prev.individuals];

      if (value === "Assessment" && individuals.length === 0) {
        individuals.push(createEmptyIndividual());
      }

      individuals = individuals.map((individual) => ({
        ...individual,
        project: value === "Assessment" ? individual.project : "",
        vertical: value === "Assessment" ? individual.vertical : "",
      }));

      return {
        ...prev,
        purpose: value,
        otherPurpose: value === "Others" ? prev.otherPurpose : "",
        individuals,
      };
    });

    return;
  }
  if (name === "reimbursementPaymentType") {
    setFormData((prev) => ({
      ...prev,
      reimbursementPaymentType: value,
      reimbursementUpiId: value === "UPI" ? prev.reimbursementUpiId : "",
      reimbursementUpiNumber:
        value === "UPI" ? prev.reimbursementUpiNumber : "",
      reimbursementAccountNumber:
        value === "Account Details" ? prev.reimbursementAccountNumber : "",
      reimbursementAccountHolderName:
        value === "Account Details" ? prev.reimbursementAccountHolderName : "",
      reimbursementSupportingDocument: null,
    }));
    return;
  }
   if (name === 'selfBooked') {
    const nextSelfBooked = value === 'true';
    setFormData(prev => ({
      ...prev,
      selfBooked: nextSelfBooked,
      ...(nextSelfBooked
        ? {}
        : {
            selfBookedTicketAmount: '',
            selfBookedTicketBill: null,
            selfBookedHotelDetails: '',
            selfBookedHotelAmount: '',
            selfBookedHotelBill: null,
            paymentMode: '',
            otherPaymentMode: '',
            paymentReference: '',
            paymentUpiNumber: '',
            paymentDate: '',
            paymentProof: null,
            reimbursementPaymentType: '',
            reimbursementUpiId: '',
            reimbursementUpiNumber: '',
            reimbursementAccountNumber: '',
            reimbursementAccountHolderName: '',
            reimbursementSupportingDocument: null,
            ticketDeclarationAccepted: false,
            accommodationDeclarationAccepted: false,
            paymentDeclarationAccepted: false,
            finalDeclarationAccepted: false,
          }),
      travelPreference: nextSelfBooked ? '' : prev.travelPreference,
      travelClass: nextSelfBooked ? '' : prev.travelClass,
      trainNumber: nextSelfBooked ? '' : prev.trainNumber,
      trainTiming: nextSelfBooked ? '' : prev.trainTiming,
      busName: nextSelfBooked ? '' : prev.busName,
      busTiming: nextSelfBooked ? '' : prev.busTiming,
      fromDate: isJourneyDateAllowed(prev.fromDate, nextSelfBooked) ? prev.fromDate : '',
      toDate:
        isJourneyDateAllowed(prev.toDate, nextSelfBooked) &&
        (!prev.fromDate || !prev.toDate || prev.toDate >= prev.fromDate)
          ? prev.toDate
          : '',
    }));

    if (!nextSelfBooked) {
      setSelfTicketBillFile(null);
      setSelfTicketBillName('');
      setSelfTicketBillUrl('');
      setSelfHotelBillFile(null);
      setSelfHotelBillName('');
      setSelfHotelBillUrl('');
      setSelfPaymentProofFile(null);
      setSelfPaymentProofName('');
      setSelfPaymentProofUrl('');
      setSelfReimbursementDocFile(null);
      setSelfReimbursementDocName('');
      setSelfReimbursementDocUrl('');
      setErrors({});
    }
    return;
  }

    if (name === 'fromDate') {
      if (
        !manualDateEditingRef.current.fromDate &&
        !isJourneyDateAllowed(value, formData.selfBooked)
      ) {
        notifyError(
          formData.selfBooked
            ? NotifyMessages.reimbursementFromDateError
            : NotifyMessages.fromDateError,
          'bottom-center'
        );
        return;
      }
    } else if (name === 'toDate') {
      if (
        !manualDateEditingRef.current.toDate &&
        !isJourneyDateAllowed(value, formData.selfBooked)
      ) {
        notifyError(
          formData.selfBooked
            ? NotifyMessages.reimbursementJourneyDateError
            : NotifyMessages.journeyDateFutureError,
          'bottom-center'
        );
        return;
      }

      if (
        !manualDateEditingRef.current.toDate &&
        formData.fromDate &&
        value < formData.fromDate
      ) {
        notifyError(NotifyMessages.toDateError, 'bottom-center');
        return;
      }
    }

    // Prevent same Source and Destination
    if (index === null) {
      if (name === "source" && isSameLocation(value, formData.destination)) {
        notifyError(REQUEST_MESSAGES.SOURCE_DESTINATION_SAME);
        return;
      }

      if (name === "destination" && isSameLocation(value, formData.source)) {
        notifyError(REQUEST_MESSAGES.SOURCE_DESTINATION_SAME);
        return;
      }

      if (name === "returnSource" && isSameLocation(value, formData.returnDestination)) {
        notifyError(REQUEST_MESSAGES.RETURN_SOURCE_DESTINATION_SAME);
        return;
      }

      if (name === "returnDestination" && isSameLocation(value, formData.returnSource)) {
        notifyError(REQUEST_MESSAGES.RETURN_SOURCE_DESTINATION_SAME);
        return;
      }
    }

      if (index !== null) {
        const updatedIndividuals = formData.individuals.map((individual, i) => {
          if (name === 'project' || name === 'vertical') {
            return {
              ...individual,
              [name]: value,
            };
          }

          if (i === index) {
            const updatedIndividual = { ...individual, [name]: value };
            if (name === 'dob') {
              updatedIndividual.age = calculateAgeLabel(value);
            }
            return updatedIndividual;
          }
          return individual;
        });
        setFormData({ ...formData, individuals: updatedIndividuals });
      }
else {
  setFormData(prev => ({
    ...prev,
    [name]: value,

    // Travel Preference
    ...(name === "travelPreference" &&
    prev.tripType === "Round Trip"
      ? {
          returnTravelPreference: value,
          returnTravelClass: '',
          returnTrainNumber: '',
          returnTrainTiming: '',
          returnBusName: '',
          returnBusTiming: ''
        }
      : {}),

    // Train Class
    ...(name === "travelClass" &&
    prev.tripType === "Round Trip"
      ? { returnTravelClass: value }
      : {}),

    // Train Number
    ...(name === "trainNumber" &&
    prev.tripType === "Round Trip"
      ? { returnTrainNumber: value }
      : {}),

    // Train Timing
    ...(name === "trainTiming" &&
    prev.tripType === "Round Trip"
      ? { returnTrainTiming: value }
      : {}),

    // Bus Name
    ...(name === "busName" &&
    prev.tripType === "Round Trip"
      ? { returnBusName: value }
      : {}),

    // Bus Timing
    ...(name === "busTiming" &&
    prev.tripType === "Round Trip"
      ? { returnBusTiming: value }
      : {}),
  }));
}
  };

  // Add a new individual to the individuals array
  const addIndividual = () => {
    setFormData((prev) => {
      const newIndividual = createEmptyIndividual();

      if (prev.purpose === "Assessment" && prev.individuals.length > 0) {
        newIndividual.project = prev.individuals[0].project || "";
        newIndividual.vertical = prev.individuals[0].vertical || "";
      }

      return {
        ...prev,
        individuals: [...prev.individuals, newIndividual],
      };
    });
  };

  // Remove an individual from the individuals array
  const removeIndividual = (index) => {
    const updatedIndividuals = formData.individuals.filter((_, i) => i !== index);
    setFormData({ ...formData, individuals: updatedIndividuals });
  };

const handleDeclarationChange = (name, checked) => {
  setFormData((prev) => ({
    ...prev,
    [name]: checked,
  }));
};

const handleReimbursementSupportingDocumentChange = (event) => {
  const selectedFile = event.target.files?.[0];
  if (!selectedFile) return;

  // NOTE: Do NOT put the raw File object into formData. formData is
  // JSON.stringify'd when updating an existing request, which turns a
  // File into "{}" and previously caused the backend to store the
  // literal string "[object Object]". Track the File locally instead,
  // the same way selfBookedTicketBill/selfBookedHotelBill/paymentProof
  // are tracked, and upload it via the dedicated upload endpoint.
  setSelfReimbursementDocFile(selectedFile);
  setSelfReimbursementDocName(selectedFile.name);
};

const handleReimbursementSupportingDocumentDelete = () => {
  setSelfReimbursementDocFile(null);
  setSelfReimbursementDocName('');
  setSelfReimbursementDocUrl('');
  setFormData((prev) => ({
    ...prev,
    reimbursementSupportingDocument: null,
  }));
};

const hasUncheckedDeclarations = () => {
  if (
    !TRAVEL_SUPPORT_TYPES.includes(
      formData.supportNeeded
    )
  ) {
    return false;
  }

  return !formData.finalDeclarationAccepted;
};

const checkFormValidity = () => {
  let valid = true;

  if (!formData.supportNeeded) valid = false;

  if (TRAVEL_SUPPORT_TYPES.includes(formData.supportNeeded)) {

    if (!formData.batchId) valid = false;
    if (!formData.center) valid = false;
    if (!formData.client) valid = false;
    if (!formData.costCenter) valid = false;
    if (!formData.purpose) valid = false;
    if (formData.purpose === "Others" && !formData.otherPurpose) valid = false;
    if (formData.costCenter === "Others" && !formData.otherCostCenter) valid = false;
    if (!formData.individuals.length) valid = false;
    formData.individuals.forEach((ind, idx) => {
      if (!ind.name) valid = false;
      if (!ind.type) valid = false;

      if (ind.type) {
        if (!ind.gender) valid = false;
        if (!ind.dob) valid = false;
        if (!/^\d{10}$/.test(ind.contactNumber)) valid = false;
      }

      // Project and Vertical are required only for Assessment.
      if (formData.purpose === "Assessment" && idx === 0) {
        if (!ind.project?.trim()) valid = false;
        if (!ind.vertical?.trim()) valid = false;
      }
    });

    if (isTravelSupport(formData.supportNeeded) && !formData.source) valid = false;
    if (!formData.destination) valid = false;
    if (!formData.fromDate) valid = false;
    if (formData.supportNeeded !== "Travel" && !formData.toDate) valid = false;
    if (
      !manualDateEditingRef.current.fromDate &&
      /^\d{4}-\d{2}-\d{2}$/.test(formData.fromDate) &&
      !isJourneyDateAllowed(formData.fromDate, formData.selfBooked)
    ) {
      valid = false;
    }

    if (
      !manualDateEditingRef.current.toDate &&
      /^\d{4}-\d{2}-\d{2}$/.test(formData.toDate) &&
      !isJourneyDateAllowed(formData.toDate, formData.selfBooked)
    ) {
      valid = false;
    }
    if (formData.fromDate && formData.toDate && formData.toDate < formData.fromDate) valid = false;

    // Round Trip validation
    if (
      isTravelSupport(formData.supportNeeded) &&
      formData.tripType === "Round Trip"
    ) {

      if (!formData.returnSource) valid = false;

      if (!formData.returnDestination) valid = false;

      if (
        formData.supportNeeded === "Travel" &&
        !formData.returnDate
      ) {
        valid = false;
      }

    }

    if (formData.selfBooked) {
      if (!formData.paymentDate) valid = false;
      if (formData.paymentMode) {
        if (
          formData.paymentMode === "Other" &&
          !formData.otherPaymentMode?.trim()
        ) {
          valid = false;
        }

        if (formData.paymentMode !== "Cash") {
          if (!formData.paymentReference) valid = false;
        }

        const hasPaymentProof = requestId
          ? Boolean(
              selfPaymentProofUrl ||
              selfPaymentProofFile ||
              formData.paymentProof
            )
          : Boolean(selfPaymentProofFile);

        if (!hasPaymentProof) valid = false;
      }
      if (TRAVEL_SUPPORT_WITH_TICKET.includes(formData.supportNeeded)) {

        if (!formData.selfBookedTicketAmount) valid = false;
        const hasTicketBill = requestId
          ? Boolean(selfTicketBillUrl || formData.selfBookedTicketBill)
          : Boolean(formData.selfBookedTicketBill);
        if (!hasTicketBill) valid = false;
      }

      if (ACCOMMODATION_SUPPORT_TYPES.includes(formData.supportNeeded)) {

        if (!formData.selfBookedHotelAmount) valid = false;
        const hasHotelBill = requestId
          ? Boolean(selfHotelBillUrl || formData.selfBookedHotelBill)
          : Boolean(formData.selfBookedHotelBill);
        if (!hasHotelBill) valid = false;
      }
    }
  }

  // Courier Support
  if (formData.supportNeeded.includes("Courier")) {
    if (!formData.courierType ||
        (formData.courierType === "Others" && !formData.otherCourierType) ||
        !formData.sentFrom ||
        !formData.receivedBy ||
        !formData.source ||
        !formData.destination) {
      valid = false;
    }

    if (formData.supportNeeded === "Receive Courier") {
      if (!formData.receiverEmail ||
          !formData.receivedOn ||
          !formData.podNumber ||
          !formData.courierCo) {
        valid = false;
      }
    }
  }

  if (hasUncheckedDeclarations()) valid = false;

  setIsFormValid(valid);
};

const findEmptyFields = () => {
  const newErrors = {};
  const mark = (key) => {
    newErrors[key] = true;
  };

  if (!formData.supportNeeded) mark("supportNeeded");

  if (
    TRAVEL_SUPPORT_TYPES.includes(
      formData.supportNeeded
    )
  ) {
    if (!formData.batchId) mark("batchId");
    if (!formData.center) mark("center");
    if (!formData.client) mark("client");
    if (!formData.costCenter) mark("costCenter");
    if (!formData.purpose) mark("purpose");

    if (formData.purpose === "Others" && !formData.otherPurpose) {
      mark("otherPurpose");
    }

    if (formData.costCenter === "Others" && !formData.otherCostCenter) {
      mark("otherCostCenter");
    }

    if (isTravelSupport(formData.supportNeeded) && !formData.source) {
      mark("source");
    }

    if (!formData.destination) mark("destination");
    if (!formData.fromDate) mark("fromDate");

    if (formData.supportNeeded !== "Travel" && !formData.toDate) {
      mark("toDate");
    }

    if (
      formData.fromDate &&
      !isJourneyDateAllowed(formData.fromDate, formData.selfBooked)
    ) {
      mark("fromDate");
    }

    if (
      formData.toDate &&
      !isJourneyDateAllowed(formData.toDate, formData.selfBooked)
    ) {
      mark("toDate");
    }

    if (
      formData.fromDate &&
      formData.toDate &&
      formData.toDate < formData.fromDate
    ) {
      mark("toDate");
    }

    if (!formData.individuals.length) {
      mark("individualList");
    }

    if (
      isTravelSupport(formData.supportNeeded) &&
      formData.tripType === "Round Trip"
    ) {
      if (!formData.returnSource) {
        mark("returnSource");
      }

      if (!formData.returnDestination) {
        mark("returnDestination");
      }

      if (
        formData.supportNeeded === "Travel" &&
        !formData.returnDate
      ) {
        mark("returnDate");
      }
    }

    formData.individuals.forEach((ind, idx) => {
      if (!ind.name) {
        mark(`individual_${idx}_name`);
      }

      if (!ind.type) {
        mark(`individual_${idx}_type`);
      }

      if (ind.type) {
        if (!ind.gender) {
          mark(`individual_${idx}_gender`);
        }

        if (!ind.dob) {
          mark(`individual_${idx}_dob`);
        }

        if (!/^\d{10}$/.test(ind.contactNumber)) {
          mark(`individual_${idx}_contactNumber`);
        }
      }

      // Project and Vertical are required only for Assessment.
      if (formData.purpose === "Assessment" && idx === 0) {
        if (!ind.project) {
          mark("individual_0_project");
        }

        if (!ind.vertical) {
          mark("individual_0_vertical");
        }
      }
    });

    if (formData.selfBooked) {
      if (!formData.paymentDate) {
        mark("paymentDate");
      }

      if (formData.paymentMode) {
        if (
          formData.paymentMode === "Other" &&
          !formData.otherPaymentMode?.trim()
        ) {
          mark("otherPaymentMode");
        }

        if (formData.paymentMode !== "Cash") {
          if (!formData.paymentReference) {
            mark("paymentReference");
          }
        }

        const hasPaymentProof = requestId
          ? Boolean(
              selfPaymentProofUrl ||
              selfPaymentProofFile ||
              formData.paymentProof
            )
          : Boolean(selfPaymentProofFile);

        if (!hasPaymentProof) {
          mark("paymentProof");
        }
      }

      if (
        TRAVEL_SUPPORT_WITH_TICKET.includes(
          formData.supportNeeded
        )
      ) {
        if (!formData.selfBookedTicketAmount) {
          mark("selfBookedTicketAmount");
        }

        const hasTicketBill = requestId
          ? Boolean(
              selfTicketBillUrl || formData.selfBookedTicketBill
            )
          : Boolean(formData.selfBookedTicketBill);

        if (!hasTicketBill) {
          mark("selfBookedTicketBill");
        }
      }

      if (
        ACCOMMODATION_SUPPORT_TYPES.includes(
          formData.supportNeeded
        )
      ) {
        if (!formData.selfBookedHotelAmount) {
          mark("selfBookedHotelAmount");
        }

        const hasHotelBill = requestId
          ? Boolean(
              selfHotelBillUrl || formData.selfBookedHotelBill
            )
          : Boolean(formData.selfBookedHotelBill);

        if (!hasHotelBill) {
          mark("selfBookedHotelBill");
        }
      }
    }
  }

  if (formData.supportNeeded?.includes("Courier")) {
    if (!formData.courierType) {
      mark("courierType");
    }

    if (
      formData.courierType === "Others" &&
      !formData.otherCourierType
    ) {
      mark("otherCourierType");
    }

    if (!formData.sentFrom) {
      mark("sentFrom");
    }

    if (!formData.receivedBy) {
      mark("receivedBy");
    }

    if (!formData.source) {
      mark("source");
    }

    if (!formData.destination) {
      mark("destination");
    }

    if (formData.supportNeeded === "Receive Courier") {
      if (!formData.receiverEmail) {
        mark("receiverEmail");
      }

      if (!formData.receivedOn) {
        mark("receivedOn");
      }

      if (!formData.podNumber) {
        mark("podNumber");
      }

      if (!formData.courierCo) {
        mark("courierCo");
      }
    }
  }

  return newErrors;
};

const clearErrorFor = (key) => {
  setErrors(prev => {
    if (!prev || !prev[key]) return prev;
    const copy = { ...prev };
    delete copy[key];
    return copy;
  });
};

const {
  handleDragOver,
  handleDragLeave,
  handlePaymentProofChange,
  handlePaymentProofDelete,
  handleSelfBookingFileChange,
  handleSelfBookingFileDelete,
  handleIdProofChange,
  handleIdProofDelete,
} = useRequestUploads({
  requestId,

  setDragTarget,

  setSelfTicketBillFile,
  setSelfTicketBillName,
  setSelfTicketBillUrl,

  setSelfHotelBillFile,
  setSelfHotelBillName,
  setSelfHotelBillUrl,

  setSelfPaymentProofFile,
  setSelfPaymentProofName,
  setSelfPaymentProofUrl,

  setFormData,
  clearErrorFor,
});

const handleAddSelectedIndividuals = () => {
  if (!selectedSavedIndividuals.length) {
    notifyError(REQUEST_MESSAGES.SELECT_SAVED_INDIVIDUAL, 'bottom-center');
    return;
  }

  const existingIndividualIds = new Set(
    formData.individuals
      .map((individual) => individual.savedInfoId)
      .filter(Boolean)
      .map(String)
  );
  const individualsToAdd = selectedSavedIndividuals.filter(
    (individual) => !existingIndividualIds.has(String(individual.value))
  );

  if (individualsToAdd.length) {
    addSelectedSavedIndividualsToForm(individualsToAdd);
    setTimeout(() => savedIndividualsSelectRef.current?.blur(), 0);
  }
};

const handleSavedIndividualsChange = (value, actionMeta) => {
  if (actionMeta.action === 'clear') {
    const clearedSavedIds = new Set(
      (actionMeta.removedValues || selectedSavedIndividuals)
        .map((individual) => individual.value)
        .filter(Boolean)
        .map(String)
    );

    setFormData((prev) => ({
      ...prev,
      individuals: prev.individuals.filter(
        (individual) => !clearedSavedIds.has(String(individual.savedInfoId))
      ),
    }));
    setSelectedSavedIndividuals([]);
    return;
  }

  setSelectedSavedIndividuals(value || []);

  const removedIndividual =
    actionMeta.action === 'remove-value'
      ? actionMeta.removedValue
      : actionMeta.action === 'deselect-option'
        ? actionMeta.removedOption
        : null;

  if (!removedIndividual) return;

  setFormData((prev) => ({
    ...prev,
    individuals: prev.individuals.filter(
      (individual) =>
        String(individual.savedInfoId) !== String(removedIndividual.value)
    ),
  }));
};

const handleSavedIndividualsMenuClose = () => {
  setSelectedSavedIndividuals((selected) =>
    selected.filter((individual) =>
      formData.individuals.some(
        (addedIndividual) =>
          String(addedIndividual.savedInfoId) === String(individual.value)
      )
    )
  );
};

const handleSaveInformation = async () => {
  if (!formData.individuals.length) {
    setErrors((prev) => ({ ...prev, individualList: true }));
    notifyError(REQUEST_MESSAGES.ADD_INDIVIDUAL_FIRST, 'bottom-center');
    return;
  }

  const missingErrors = {};
  formData.individuals.forEach((individual, index) => {
    if (!individual.name) missingErrors[`individual_${index}_name`] = true;
    if (!individual.type) missingErrors[`individual_${index}_type`] = true;
    if (!individual.gender) missingErrors[`individual_${index}_gender`] = true;
    if (!individual.dob) missingErrors[`individual_${index}_dob`] = true;
    if (!/^\d{10}$/.test(individual.contactNumber)) {
      missingErrors[`individual_${index}_contactNumber`] = true;
    }
  });

  if (Object.keys(missingErrors).length > 0) {
    setErrors((prev) => ({ ...prev, ...missingErrors }));
    notifyError(REQUEST_MESSAGES.COMPLETE_INDIVIDUAL_DETAILS, 'bottom-center');
    return;
  }

  try {
    setSavingInformation(true);
    const payload = new FormData();
    const serializedIndividuals = formData.individuals.map((individual, index) => {
      const { idProof, idProofUrl, age, ...rest } = individual;
      if (idProof) {
        payload.append(`idProof_${index}`, idProof);
      }

      return {
        ...rest,
        existingIdProof:
          individual.existingIdProof ||
          individual.idProofUrl ||
          '',
      };
    });

    payload.append('individuals', JSON.stringify(serializedIndividuals));

    const response = await fetch(`${process.env.REACT_APP_API_URL}/profile/saved-individuals`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
      },
      body: payload,
    });

    if (!response.ok) {
      const errorText = await response.text();
      let parsedMessage = errorText;
      try {
        parsedMessage = JSON.parse(errorText)?.error || errorText;
      } catch (parseError) {
        parsedMessage = errorText;
      }
      throw new Error(parsedMessage || REQUEST_MESSAGES.SAVE_INDIVIDUAL_FAILED);
    }

    const responseData = await response.json();
    const persistedIndividuals = responseData.updatedIndividuals || [];
    const allSavedIndividuals = responseData.savedIndividuals || [];

    setFormData((prev) => ({
      ...prev,
      individuals: prev.individuals.map((individual, index) => {
        const persisted = persistedIndividuals[index];
        if (!persisted) return individual;

        return {
          ...individual,
          savedInfoId: persisted.id,
          existingIdProof: persisted.idProof || individual.existingIdProof,
          idProofUrl: persisted.idProof || individual.idProofUrl,
          idProof: null,
          age: persisted.ageDisplay || calculateAgeLabel(persisted.dob),
        };
      }),
    }));

    setSavedIndividuals(allSavedIndividuals);
    localStorage.setItem(
      getSavedIndividualsStorageKey(user?.email),
      JSON.stringify(allSavedIndividuals)
    );
    window.dispatchEvent(
      new CustomEvent('routeplanner:saved-individuals-updated', {
        detail: { savedIndividuals: allSavedIndividuals },
      })
    );
    notifySuccess(REQUEST_MESSAGES.SAVE_INDIVIDUAL_SUCCESS, 'bottom-center');
  } catch (error) {
    console.error('Save information failed:', error);
    notifyError(String(error.message || REQUEST_MESSAGES.SAVE_INDIVIDUAL_FAILED), 'bottom-center');
  } finally {
    setSavingInformation(false);
  }
};

  // Handle form submission
const handleSubmit = async (e) => {
  e.preventDefault();

  if (hasUncheckedDeclarations()) {
    notifyError(REQUEST_MESSAGES.DECLARATION_BEFORE_SUBMIT, 'bottom-center');
    return;
  }
const validationErrors = findEmptyFields();

if (Object.keys(validationErrors).length > 0) {
  setErrors(validationErrors);
  notifyError(REQUEST_MESSAGES.MANDATORY_FIELDS_REQUIRED, 'bottom-center');
  return;
}
  // Prevent same Source and Destination
  if (
    formData.source &&
    formData.destination &&
    formData.source.trim().toLowerCase() ===
      formData.destination.trim().toLowerCase()
  ) {
    notifyError(REQUEST_MESSAGES.SOURCE_DESTINATION_SAME, "bottom-center");
    return;
  }
  if (requestId) {
    try {
      /* BUILD PAYLOAD (NO FILE UPLOAD HERE) */
     let payload = { ...formData };
      if (selfTicketBillFile) {
        payload.selfBookedTicketBill = '';
      }
      if (selfHotelBillFile) {
        payload.selfBookedHotelBill = '';
      }
      if (selfReimbursementDocFile) {
        // A new file is pending upload via uploadSelfBookingFile below.
        // Never let the raw File object reach JSON.stringify here -
        // that is what previously became the literal string "[object Object]".
        payload.reimbursementSupportingDocument = '';
      }

// If NOT courier → handle individuals
if (!formData.supportNeeded.includes("Courier")) {
  payload.individuals = formData.individuals.map((ind) => ({
    savedInfoId: ind.savedInfoId || null,
    name: ind.name,
    type: ind.type,
    project: ind.project,
    vertical: ind.vertical,
    gender: ind.gender,
    dob: ind.dob,
    idType: ind.idType,
    idNumber: ind.idNumber,
    contactNumber: ind.contactNumber,
    idProof: ind.idProof ? "NEW_UPLOAD" : ind.existingIdProof || null,
  }));
} else {
  // Courier request → ensure no individuals goes to backend
  delete payload.individuals;
}

      const response = await fetch(
  `${process.env.REACT_APP_API_URL}/requests/${requestId}`,
  {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(payload),
  }
);
      if (!response.ok) {
        const errorText = await response.text();

        throw new Error(
          `Request update failed: ${response.status} - ${errorText}`
        );
      }

      if (selfTicketBillFile) {
        await uploadSelfBookingFile(selfTicketBillFile, 'ticket', 'Ticket bill');
      }

      if (selfHotelBillFile) {
        await uploadSelfBookingFile(selfHotelBillFile, 'hotel', 'Hotel bill');
      }

      if (selfPaymentProofFile) {
        await uploadSelfBookingFile(selfPaymentProofFile, 'payment-proof', 'Payment proof');
      }

      if (selfReimbursementDocFile) {
        await uploadSelfBookingFile(
          selfReimbursementDocFile,
          'reimbursement-supporting-document',
          'Reimbursement supporting document'
        );
      }

      dispatchInboxRefresh();
      notifySuccess(NotifyMessages.requestEditSuccess, 'bottom-center');
      setTimeout(() => navigate('/'), 2000);
    } catch (error) {
      console.error('Edit request failed:', error);

      notifyError(
        error.message || REQUEST_MESSAGES.REQUEST_EDIT_FAILED,
        'bottom-center'
      );
    }

    return;
  }

  try {
    const formDataToSend = new FormData();

    /* ✅ ID PROOF FILES */
    const individualsCopy = formData.individuals.map((ind, i) => {
      const { idProof, idProofUrl, existingIdProof, age, ...rest } = ind;

      if (idProof) {
        formDataToSend.append(`idProof_${i}`, idProof);
      }

      return {
        ...rest,
        idProof: idProof ? 'NEW_UPLOAD' : existingIdProof || idProofUrl || null,
      };
    });

    /* ✅ APPEND FORM DATA */
    for (const key in formData) {
      if (key === 'individuals') {
        formDataToSend.append(
          'individuals',
          JSON.stringify(individualsCopy)
        );
      } else if (key === 'reimbursementSupportingDocument') {
        // Handled explicitly below so the real File object (if any) is
        // appended, instead of the generic loop stringifying it.
      } else {
        formDataToSend.append(key, formData[key]);
      }
    }

    if (selfReimbursementDocFile) {
      formDataToSend.append('reimbursementSupportingDocument', selfReimbursementDocFile);
    }

    let response;

    /* Courier flow stays JSON */
    if (formData.supportNeeded.includes('Courier')) {
      response = await fetch(
        `${process.env.REACT_APP_API_URL}/requests/courier-details`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(formData),
        }
      );
    } else {
      response = await fetch(
        `${process.env.REACT_APP_API_URL}/requests`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
          },
          body: formDataToSend,
        }
      );
    }

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(
        `Network response was not ok: ${response.status} - ${errorText}`
      );
    }

    const responseData = await response.json();
    dispatchInboxRefresh();
    notifySuccess(NotifyMessages.requestSubmitSuccess, 'bottom-center');

    if (
      TRAVEL_SUPPORT_TYPES.includes(
        formData.supportNeeded
      )
    ) {
      setTimeout(
        () => navigate(`/planner/${responseData.id}`),
        2000
      );
    } else {
      setTimeout(() => navigate('/'), 2000);
    }
  } catch (error) {
    console.error('Create request failed:', error);
  }
};

useEffect(() => {
  checkFormValidity();
}, [formData, selfTicketBillUrl, selfHotelBillUrl, selfPaymentProofUrl, selfPaymentProofFile]);
const savedIndividualOptions = savedIndividuals.map((individual) => ({
  value: individual.id,
  label: individual.name,
  data: individual,
}));
const availableSavedIndividualOptions = savedIndividualOptions.filter(
  (option) =>
    !formData.individuals.some(
      (individual) =>
        String(individual.savedInfoId) === String(option.value)
    )
);

const filterSavedIndividualOption = (option, inputValue) => {
  const query = String(inputValue || '').trim().toLowerCase();
  if (!query) return true;

  const savedIndividual = option.data.data;
  return [savedIndividual.name, savedIndividual.idNumber, savedIndividual.contactNumber]
    .filter(Boolean)
    .some((value) => String(value).toLowerCase().includes(query));
};

if (!isLoaded) return <div>Loading…</div>;
  // Conditional rendering based on supportNeeded

const renderAddressField = ({
  label,
  name,
  value,
  error,
  autocompleteRef,
  onPlaceChanged,
  popupField,
  saved,
}) => (
  <label>
    {label}

    <div className="address-field">
      <Autocomplete
        onLoad={(autocomplete) => {
          autocompleteRef.current = autocomplete;
        }}
        onPlaceChanged={onPlaceChanged}
        options={{ fields: ["formatted_address"] }}
      >
        <input
          type="text"
          name={name}
          value={value}
          onChange={handleInputChange}
          placeholder={`Enter ${label.toLowerCase()}...`}
          required
          className={error ? "error-field" : ""}
        />
      </Autocomplete>

      <div className="address-actions">
        <button
          type="button"
          className="address-action-btn"
          onClick={() => openSavedAddressPopup(popupField)}
          title="Saved Addresses"
        >
          <FaRegAddressBook />
        </button>

        <button
          type="button"
          disabled={!value.trim()}
          onClick={() =>
            saved
              ? removeSavedAddress(popupField)
              : saveAddress(popupField)
          }
          title={
            saved
              ? "Remove Saved Address"
              : "Save Address"
          }
          className={`address-action-btn bookmark-btn ${
            saved ? "saved" : "active"
          }`}
        >
          {saved ? <FaBookmark /> : <FaRegBookmark />}
        </button>
      </div>
    </div>
  </label>
);

const renderTravelPreferenceSection = () => (
  <>
    {!formData.selfBooked && (
      <label>
        Travel Preference:
        <select
          name="travelPreference"
          value={formData.travelPreference}
          onChange={handleInputChange}
          className={errors.travelPreference ? "error-field" : ""}
        >
          <option value="">Select Preference</option>
          {TRAVEL_PREFERENCE_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </label>
    )}

    {!formData.selfBooked &&
      formData.travelPreference &&
      formData.travelPreference !== "Own Vehicle" && (
      <label>
        Travel Class (Optional):
        <select
          name="travelClass"
          value={formData.travelClass}
          onChange={handleInputChange}
        >
          <option value="">Select Class</option>
          {getTravelClassOptions(formData.travelPreference).map((opt) => (
            <option key={opt} value={opt}>
              {opt}
            </option>
          ))}
        </select>
      </label>
    )}

    {!formData.selfBooked && formData.travelPreference === "Train" && (
      <>
        <label>
          Train Number:
          <input
            type="text"
            name="trainNumber"
            value={formData.trainNumber}
            onChange={handleInputChange}
            placeholder="Enter train number"
            className={errors.trainNumber ? "error-field" : ""}
          />
        </label>

        <label>
          Train Timing:
          <input
            type="time"
            name="trainTiming"
            value={formData.trainTiming}
            onChange={handleInputChange}
            step="60"
            className={errors.trainTiming ? "error-field" : ""}
          />
        </label>
      </>
    )}

    {!formData.selfBooked && formData.travelPreference === "Bus" && (
      <>
        <label>
          Bus Name:
          <input
            type="text"
            name="busName"
            value={formData.busName}
            onChange={handleInputChange}
            placeholder="Enter bus name"
            className={errors.busName ? "error-field" : ""}
          />
        </label>

        <label>
          Bus Timing:
          <input
            type="time"
            name="busTiming"
            value={formData.busTiming}
            onChange={handleInputChange}
            step="60"
            className={errors.busTiming ? "error-field" : ""}
          />
        </label>
      </>
    )}
  </>
);

const renderTripType = () => (
  <label>
    Trip Type:
    <div className="trip-type-container">
      <label className="trip-type-label">
        <input
          type="radio"
          name="tripType"
          value="One Way"
          checked={formData.tripType === "One Way"}
          onChange={handleInputChange}
          className="trip-type-radio"
        />
        One Way
      </label>

      <label className="trip-type-label">
        <input
          type="radio"
          name="tripType"
          value="Round Trip"
          checked={formData.tripType === "Round Trip"}
          onChange={handleInputChange}
          className="trip-type-radio"
        />
        Round Trip
      </label>
    </div>
  </label>
);

const renderReturnRouteFields = () => (
  <>
    {renderAddressField({
      label: "Source:",
      name: "returnSource",
      value: formData.returnSource,
      error: errors.returnSource,
      autocompleteRef: returnOriginAutocompleteRef,
      onPlaceChanged: handleReturnOriginPlaceChanged,
      popupField: "returnSource",
      saved: savedAddressStatus.returnSource,
    })}

    {renderAddressField({
      label: "Destination:",
      name: "returnDestination",
      value: formData.returnDestination,
      error: errors.returnDestination,
      autocompleteRef: returnDestinationAutocompleteRef,
      onPlaceChanged: handleReturnDestinationPlaceChanged,
      popupField: "returnDestination",
      saved: savedAddressStatus.returnDestination,
    })}
  </>
);

const renderRemarksField = () => (
  <label>
    Remarks(Optional):
    <input
      type="text"
      name="requesterRemarks"
      value={formData.requesterRemarks}
      onChange={handleInputChange}
    />
  </label>
);

const renderAccommodationDates = () => (
  <>
    <label>
      From Date:
      <input
        type="date"
        name="fromDate"
        value={formData.fromDate}
        onChange={handleInputChange}
        onPointerDown={() => handleDatePointerDown('fromDate')}
        onKeyDown={(e) => handleDateKeyDown(e, 'fromDate')}
        onBlur={() => handleDateBlur('fromDate')}
        required
        className={errors.fromDate ? "error-field" : ""}
      />
    </label>

    <label>
      To Date:
      <input
        type="date"
        name="toDate"
        value={formData.toDate}
        onChange={handleInputChange}
        onPointerDown={() => handleDatePointerDown('toDate')}
        onKeyDown={(e) => handleDateKeyDown(e, 'toDate')}
        onBlur={() => handleDateBlur('toDate')}
        required
        className={errors.toDate ? "error-field" : ""}
      />
    </label>
  </>
);

const renderTravelDate = () => (
  <label>
    Date of Journey:
    <input
      type="date"
      name="fromDate"
      value={formData.fromDate}
      onChange={handleInputChange}
      onPointerDown={() => handleDatePointerDown('fromDate')}
      onKeyDown={(e) => handleDateKeyDown(e, 'fromDate')}
      onBlur={() => handleDateBlur('fromDate')}
      min={formData.selfBooked ? formData.paymentDate : journeyDateMin}
      max={journeyDateMax}
      required
      className={errors.fromDate ? "error-field" : ""}
    />
  </label>
);

const renderReturnJourney = ({ includeReturnDate, hideWhenSelfBooked }) => (
  <>
    <h3 style={{ marginBottom: "8px" }}>Return Journey</h3>

    {renderReturnRouteFields()}

    {(!hideWhenSelfBooked || !formData.selfBooked) && (
      <label>
        Travel Preference:
        <select
          name="returnTravelPreference"
          value={formData.returnTravelPreference}
          onChange={handleInputChange}
          className={errors.returnTravelPreference ? "error-field" : ""}
        >
          <option value="">Select Preference</option>
          {TRAVEL_PREFERENCE_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </label>
    )}

    {(!hideWhenSelfBooked || !formData.selfBooked) &&
      formData.returnTravelPreference &&
      formData.returnTravelPreference !== "Own Vehicle" && (
        <label>
          Travel Class (Optional):
          <select
            name="returnTravelClass"
            value={formData.returnTravelClass}
            onChange={handleInputChange}
          >
            <option value="">Select Class</option>
            {getTravelClassOptions(formData.returnTravelPreference).map((opt) => (
              <option key={opt} value={opt}>
                {opt}
              </option>
            ))}
          </select>
        </label>
      )}

    {formData.returnTravelPreference === "Train" && (
      <>
        <label>
          Train Number:
          <input
            type="text"
            name="returnTrainNumber"
            value={formData.returnTrainNumber}
            onChange={handleInputChange}
            placeholder="Enter train number"
            className={errors.returnTrainNumber ? "error-field" : ""}
          />
        </label>

        <label>
          Train Timing:
          <input
            type="time"
            name="returnTrainTiming"
            value={formData.returnTrainTiming}
            onChange={handleInputChange}
            className={errors.returnTrainTiming ? "error-field" : ""}
            step="60"
          />
        </label>
      </>
    )}

    {formData.returnTravelPreference === "Bus" && (
      <>
        <label>
          Bus Name:
          <input
            type="text"
            name="returnBusName"
            value={formData.returnBusName}
            onChange={handleInputChange}
            className={errors.returnBusName ? "error-field" : ""}
            placeholder="Enter bus name"
          />
        </label>

        <label>
          Bus Timing:
          <input
            type="time"
            name="returnBusTiming"
            value={formData.returnBusTiming}
            onChange={handleInputChange}
            className={errors.returnBusTiming ? "error-field" : ""}
            step="60"
          />
        </label>
      </>
    )}

    {includeReturnDate && (
      <label>
        Date of Journey:
        <input
          type="date"
          name="returnDate"
          value={formData.returnDate}
          onChange={handleInputChange}
          min={formData.fromDate}
          className={errors.returnDate ? "error-field" : ""}
        />
      </label>
    )}
  </>
);

const renderTravelLayout = ({
  includeAccommodationDates = false,
  includeTravelDate = false,
  includeReturnDate = false,
  hideReturnWhenSelfBooked = false,
}) => (
  <fieldset>
    <legend>Route Details:</legend>

    <br />

    {renderTripType()}

    {formData.tripType === "Round Trip" && (
      <h3 className="going-journey-heading">
        Going Journey
      </h3>
    )}

    {renderAddressField({
      label: "Source:",
      name: "source",
      value: formData.source,
      error: errors.source,
      autocompleteRef: originAutocompleteRef,
      onPlaceChanged: handleOriginPlaceChanged,
      popupField: "source",
      saved: savedAddressStatus.source,
    })}

    {renderAddressField({
      label: "Destination:",
      name: "destination",
      value: formData.destination,
      error: errors.destination,
      autocompleteRef: destinationAutocompleteRef,
      onPlaceChanged: handleDestinationPlaceChanged,
      popupField: "destination",
      saved: savedAddressStatus.destination,
    })}

    {renderTravelPreferenceSection()}

    {includeTravelDate && renderTravelDate()}

    {includeAccommodationDates &&
      renderAccommodationDates()}

    {renderRemarksField()}

    {formData.tripType === "Round Trip" &&
      renderReturnJourney({
        includeReturnDate,
        hideWhenSelfBooked: hideReturnWhenSelfBooked,
      })}
  </fieldset>
);

  const renderFieldsBasedOnSupport = () => {
    switch (formData.supportNeeded) {
      case 'Travel':
        return renderTravelLayout({
          includeTravelDate: true,
          includeReturnDate: true,
        });
      case 'Accommodation':
        return (
          <>
            <fieldset>
              <legend>Route Details:</legend>

              {renderAddressField({
                label: "Location:",
                name: "destination",
                value: formData.destination,
                error: errors.destination,
                autocompleteRef: destinationAutocompleteRef,
                onPlaceChanged: handleDestinationPlaceChanged,
                popupField: "destination",
                saved: savedAddressStatus.destination,
              })}
              {renderAccommodationDates()}
              {renderRemarksField()}
            </fieldset>
          </>
        );
      case 'Travel + Accommodation':
      return renderTravelLayout({
        includeAccommodationDates: true,
        hideReturnWhenSelfBooked: true,
      });
      default:
        return null;
    }
  };

const renderCourierBasicFields = () => (
  <>
    <label>
      Courier Type:
      <select
        name="courierType"
        value={formData.courierType}
        onChange={handleInputChange}
        required
        className={errors.courierType ? "error-field" : ""}
      >
        <option value="">Select Courier-type</option>
        <option value="Document">Document</option>
        <option value="Package">Package</option>
        <option value="Others">Others</option>
      </select>
    </label>

    {formData.courierType === "Others" && (
      <label>
        Mention Courier-type if selected Others:
        <input
          type="text"
          name="otherCourierType"
          value={formData.otherCourierType}
          onChange={handleInputChange}
          required
          className={errors.otherCourierType ? "error-field" : ""}
        />
      </label>
    )}

    <label>
      Sender Name:
      <input
        type="text"
        name="sentFrom"
        value={formData.sentFrom}
        onChange={handleInputChange}
        required
        className={errors.sentFrom ? "error-field" : ""}
      />
    </label>

    <label>
      Receiver Name:
      <input
        type="text"
        name="receivedBy"
        value={formData.receivedBy}
        onChange={handleInputChange}
        required
        className={errors.receivedBy ? "error-field" : ""}
      />
    </label>
  </>
);

const renderCourierAddressFields = () => (
  <>
    {renderAddressField({
      label: "Complete Source Address",
      name: "source",
      value: formData.source,
      error: errors.source,
      autocompleteRef: originAutocompleteRef,
      onPlaceChanged: handleOriginPlaceChanged,
      popupField: "source",
      saved: savedAddressStatus.source,
    })}

    {renderAddressField({
      label: "Complete Destination Address",
      name: "destination",
      value: formData.destination,
      error: errors.destination,
      autocompleteRef: destinationAutocompleteRef,
      onPlaceChanged: handleDestinationPlaceChanged,
      popupField: "destination",
      saved: savedAddressStatus.destination,
    })}
  </>
);

// Conditional rendering based on supportNeeded
const renderCourierSupport = () => {
  switch (formData.supportNeeded) {
    case 'Send Courier':
      return (
        <>
          <fieldset>
            <legend>Send Courier:</legend>
            {renderCourierBasicFields()}
          {renderCourierAddressFields()}
          {renderRemarksField()}
          </fieldset>
        </>
      );
    case 'Receive Courier':
      return (
        <>
          <fieldset>
          <legend>Recieve Courier:</legend>
          {renderCourierBasicFields()}
            <label>
              Receiver Email:
              <input
                type="email"
                name="receiverEmail"
                value={formData.receiverEmail}
                onChange={handleInputChange}
                required
                className={errors.receiverEmail ? "error-field" : ""}

              />
            </label>
            {renderCourierAddressFields()}
            <label>
              Received On:
              <input
                type="date"
                name="receivedOn"
                value={formData.receivedOn}
                onChange={handleInputChange}
                required
                className={errors.receivedOn ? "error-field" : ""}

              />
            </label>
            <label>
              Proof of Delivery(POD) Number:
              <input
                type="text"
                name="podNumber"
                value={formData.podNumber}
                onChange={handleInputChange}
                required
                className={errors.podNumber ? "error-field" : ""}

              />
            </label>
            <label>
              Courier Company:
              <input
                type="text"
                name="courierCo"
                value={formData.courierCo}
                onChange={handleInputChange}
                required
                className={errors.courierCo ? "error-field" : ""}

              />
            </label>
            {renderRemarksField()}
          </fieldset>
        </>
      );
    default:
      return null;
  }
};
  return (
    <div className="Request">
      <Header />
      <ToastContainer />
      <main className="main-content">
         <div className="scroll-card">
    <div className="card-content">
        <div className="form-header">
  <div className="back-icon" onClick={() => navigate(-1)}>
    <IoMdArrowBack />
  </div>
  </div>
        <form className="reqForm" onSubmit={handleSubmit}>
          <h1>{requestId ? 'Edit Request' : 'Request Details'}</h1>

          <fieldset>
            <legend>Requested For:</legend>
            <label
              className={`support-needed-field ${
                formData.supportNeeded ? "has-selection" : "no-selection"
              }`}
            >
              Support Needed:
              <select
                name="supportNeeded"
                value={formData.supportNeeded}
                onChange={handleInputChange}
                required
                className={errors.supportNeeded ? "error-field" : ""}
              >
                <option value="">Select the support needed</option>
                <option value="Travel">Travel</option>
                <option value="Accommodation">Accommodation</option>
                <option value="Travel + Accommodation">Travel + Accommodation</option>
                <option value="Send Courier">Send Courier</option>
                <option value="Receive Courier">Receive Courier</option>
                
              </select>
            </label>
            
            {(formData.supportNeeded.includes('Courier'))
            && renderCourierSupport()}

            {((formData.supportNeeded==='Travel') || (formData.supportNeeded==='Accommodation') || (formData.supportNeeded==='Travel + Accommodation' ))
            &&
            <>
            <label>
              Batch ID:
              <input
                type="text"
                name="batchId"
                value={formData.batchId}
                onChange={handleInputChange}
                required
                className={errors.batchId ? "error-field" : ""}
              />
            </label>
            <label>
              Center:
              <input
                type="text"
                name="center"
                value={formData.center}
                onChange={handleInputChange}
                required
                className={errors.center ? "error-field" : ""}

              />
            </label>
            <label>
              Client:
              <input
                type="text"
                name="client"
                value={formData.client}
                onChange={handleInputChange}
                required
                className={errors.client ? "error-field" : ""}

              />
            </label>
            <label>
              Cost Center:
              <select
                name="costCenter"
                value={formData.costCenter}
                onChange={handleInputChange}
                required
                className={errors.costCenter ? "error-field" : ""}

              >
                <option value="">Select Cost-Center</option>
                <option value="Awarding Bodies">Awarding Bodies</option>
                <option value="Corporate Backed Foundations (CBF)">Corporate Backed Foundations (CBF)</option>
                <option value="Educational & Skilling Institutions (ESI)">Educational & Skilling Institutions (ESI)</option>
                <option value="Platform Services">Platform Services</option>
                <option value="Others">Others</option>
              </select>
            </label>
            {formData.costCenter === 'Others' && (
              <label>
                Mention Cost-Center if selected Others:
                <input
                  type="text"
                  name="otherCostCenter"
                  value={formData.otherCostCenter}
                  onChange={handleInputChange}
                  required
                  className={errors.otherCostCenter ? "error-field" : ""}

                />
              </label>
            )}
            <label>
              Purpose:
              <select
                name="purpose"
                value={formData.purpose}
                onChange={handleInputChange}
                required
                className={errors.purpose ? "error-field" : ""}
              >
                <option value="">Select Purpose</option>
                <option value="Assessment">Assessment</option>
                <option value="Meeting">Meeting</option>
                <option value="Others">Others</option>
              </select>
            </label>
            {formData.purpose === 'Others' && (
              <label>
                Mention Purpose if selected Others:
                <input
                  type="text"
                  name="otherPurpose"
                  value={formData.otherPurpose}
                  onChange={handleInputChange}
                  required
                  className={errors.otherPurpose ? "error-field" : ""}
                />
              </label>
            )}
            {formData.purpose === 'Assessment' && formData.individuals.length > 0 && (
              <div className="purpose-linked-fields">
                <label>
                  Project:
                  <input
                    type="text"
                    name="project"
                    value={formData.individuals[0].project}
                    onChange={(e) => handleInputChange(e, 0)}
                    required
                    className={errors["individual_0_project"] ? "error-field" : ""}
                  />
                </label>
                <label>
                  Vertical:
                  <input
                    type="text"
                    name="vertical"
                    value={formData.individuals[0].vertical}
                    onChange={(e) => handleInputChange(e, 0)}
                    required
                    className={errors["individual_0_vertical"] ? "error-field" : ""}
                  />
                </label>
              </div>
            )}
            </>}
          </fieldset>

          {((formData.supportNeeded==='Travel') || (formData.supportNeeded==='Accommodation') || (formData.supportNeeded==='Travel + Accommodation' ))
            &&
            <>
                <fieldset    className={`saved-individuals-section ${
                  errors.individualList ? "error-fieldset" : ""
                }`}>
                  <legend className="saved-individuals-label">
                    Add Individual(s):
                  </legend>

                  <div className="saved-individual-row">
                     <div className="saved-select-container">
                    <Select
                      ref={savedIndividualsSelectRef}
                      isMulti
                      isSearchable
                      options={availableSavedIndividualOptions}
                      value={selectedSavedIndividuals}
                      onChange={handleSavedIndividualsChange}
                      onMenuClose={handleSavedIndividualsMenuClose}
                      classNamePrefix="saved-select"
                      placeholder={
                        savedIndividualsLoading
                          ? "Loading saved individuals..."
                          : "Select individual(s)"
                      }
                      noOptionsMessage={() =>
                        savedIndividualsLoading
                          ? "Loading..."
                          : "No saved individuals found"
                      }
                      components={{ Option: SavedIndividualOption }}
                      closeMenuOnSelect={false}
                      hideSelectedOptions={false}
                      filterOption={filterSavedIndividualOption}
                    />
                      </div>
                    <button
                      type="button"
                      onMouseDown={(event) => event.preventDefault()}
                      onClick={handleAddSelectedIndividuals}
                      className="saved-add-button"
                    >
                      Add
                    </button>
                  </div>
                    {formData.individuals.length === 0 && (
                      <>
                        <div className="individual-divider">
                            <span>OR</span>
                        </div>

                        <button
                            type="button"
                            onClick={addIndividual}
                            className="initial-add-button"
                          >
                            + Add Individual
                        </button>
                      </> 
                    )}
                
                
            {errors.individualList && (
              <p className="error-text">{REQUEST_MESSAGES.ADD_INDIVIDUAL_REQUIRED}</p>
            )}
            {formData.individuals.length > 0 && (
              <>
            {formData.individuals.map((individual, index) => (
              <div key={index} className="individual">
                <h3>Individual {index + 1}</h3>
                <label>
                  Individual Name:
                  <input
                    type="text"
                    name="name"
                    value={individual.name}
                    onChange={(e) => handleInputChange(e, index)}
                    required
                    className={errors[`individual_${index}_name`] ? "error-field" : ""}

                  />
                </label>
                <label>
                  Individual Type:
                  <select
                    name="type"
                    value={individual.type}
                    onChange={(e) => handleInputChange(e, index)}
                    required
                    className={errors[`individual_${index}_type`] ? "error-field" : ""}

                  >
                    <option value="">Select Individual type</option>
                    <option value="Assessor">Assessor</option>
                    <option value="Proctor">Proctor</option>
                    <option value="SME">SME</option>
                    <option value="Employee">Employee</option>
                    <option value="Employee_Assessor">Employee-Assessor</option>
                    <option value="Trainer">Trainer</option>
                    <option value="Counselor">Counselor</option>
                    <option value="Intern">Intern</option>
                    <option value="Freelancer">Freelancer</option>
                  </select>
                </label>
                   
  <>
    <label>
      Gender:
      <select
        name="gender"
        value={individual.gender}
        onChange={(e) => handleInputChange(e, index)}
        required
        className={errors[`individual_${index}_gender`] ? "error-field" : ""}
      >
        <option value="">Select Gender</option>
        <option value="Male">Male</option>
        <option value="Female">Female</option>
        <option value="Others">Others</option>
        <option value="Not Disclosed">Prefer not to disclose</option>
      </select>
    </label>

    <label>
      DOB:
      <input
        type="date"
        name="dob"
        value={individual.dob}
        onChange={(e) => handleInputChange(e, index)}
        required
        className={errors[`individual_${index}_dob`] ? "error-field" : ""}
      />
    </label>

    <label>
      Age:
      <input
        type="text"
        name="age"
        value={individual.age || calculateAgeLabel(individual.dob)}
        readOnly
      />
    </label>

    <label>
      ID Type:
      <select
        name="idType"
        value={individual.idType}
        onChange={(e) => handleInputChange(e, index)}
        className={errors[`individual_${index}_idType`] ? "error-field" : ""}
      >
        <option value="">Select ID Type</option>
        <option value="Aadhaar">Aadhaar</option>
        <option value="Voter ID">Voter ID</option>
        <option value="Passport">Passport</option>
        <option value="Driving License">Driving License</option>
        <option value="PAN">PAN</option>
        <option value="Other Government ID">Other Government ID</option>
      </select>
    </label>

    <label>
      ID Number:
      <input
        type="text"
        name="idNumber"
        value={individual.idNumber}
        onChange={(e) => handleInputChange(e, index)}
        placeholder="Enter ID number"
        className={errors[`individual_${index}_idNumber`] ? "error-field" : ""}
      />
    </label>

<div
  className={`upload ${dragTarget === `idProof-${index}` ? "drag-active" : ""}`}
  onDragOver={(e) => handleDragOver(e, `idProof-${index}`)}
  onDragLeave={handleDragLeave}
  onDrop={(e) => {
    e.preventDefault();
    setDragTarget(null);

    handleIdProofChange(
      {
        target: {
          files: e.dataTransfer.files,
        },
      },
      index
    );
  }}
>
  <label
    htmlFor={`idProof_${index}`}
    className="upload-button"
  >
    <span className="required-label">
      ID Proof:
    </span>

    {!individual.idProofUrl && <FaCloudUploadAlt size={20} />}

    <span>
      {individual.idProofUrl ? (
        <span className="view-download">
          <a
            href={individual.idProofUrl}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) =>
              handleSecureLinkOpen(
                e,
                individual.existingIdProof || individual.idProofUrl,
                individual.idProof?.name || getCleanFileName(individual.idProofUrl)
              )
            }
            style={{ color: "#224D3D", fontWeight: "600" }}
          >
           {getCleanFileName(individual.idProof?.name || individual.idProofUrl)}

          </a>
        </span>
      ) : (
        <p className="upload-icon">Upload</p>
      )}
    </span>

    <input
      type="file"
      id={`idProof_${index}`}
      accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
      onChange={(e) => handleIdProofChange(e, index)}
    />
    <small className="upload-size-hint">Maximum file size: 10 MB</small>
  </label>

  {individual.idProofUrl && (
    <span className="delete-upload">
      <MdDelete onClick={() => handleIdProofDelete(index)} />
    </span>
  )}
</div>

<label className="contact-field">
  Contact Number:
  <input
    type="text"
    name="contactNumber"
    inputMode="numeric"
    maxLength="10"
    value={individual.contactNumber}
    onChange={(e) => {
      const value = e.target.value;

      if (/^\d*$/.test(value)) {
        setDigitError(prev => ({ ...prev, [index]: false }));
        handleInputChange(
          { target: { name: 'contactNumber', value } },
          index
        );
      } else {
        setDigitError(prev => ({ ...prev, [index]: true }));
      }
    }}
    onKeyDown={(e) => {
      if (
        !/[0-9]/.test(e.key) &&
        !['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab'].includes(e.key)
      ) {
        e.preventDefault();
        setDigitError(prev => ({ ...prev, [index]: true }));
      }
    }}
    required
    className={
      errors[`individual_${index}_contactNumber`] || digitError[index]
        ? "error-field"
        : ""
    }
  />

  {digitError[index] && (
    <span className="inline-error-below">{REQUEST_MESSAGES.DIGITS_ONLY}</span>
  )}
</label>

  </>
                <button
                  type="button"
                  onClick={() => removeIndividual(index)}
                  className="delete-button"
                >
                  Delete
                </button>
              </div>
            ))}
            <div className="individual-action-row">
              <button type="button" onClick={addIndividual} className="add-button">
                Add Individual
              </button>
              <button
                type="button"
                onClick={handleSaveInformation}
                className="save-info-button"
                disabled={savingInformation}
              >
                {savingInformation ? 'Saving...' : 'Save Information'}
              </button>
            </div>
            </>
            )}
            </fieldset >
            <fieldset>
            <legend>Self-Booking and Reimbursement:</legend>
            <label>
              Self-booked Offline:
              <select
                name="selfBooked"
                value={formData.selfBooked ? 'true' : 'false'}
                onChange={handleInputChange}
              >
                <option value="false">No</option>
                <option value="true">Yes</option>
              </select>
            </label>
            {formData.selfBooked && (
              <>
                {((formData.supportNeeded==='Travel') || (formData.supportNeeded==='Travel + Accommodation')) && (
                  <>
                    <label>
                      Ticket Amount:
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        name="selfBookedTicketAmount"
                        value={formData.selfBookedTicketAmount}
                        onChange={handleInputChange}
                        className={errors.selfBookedTicketAmount ? "error-field" : ""}
                        required
                      />
                    </label>
                    <div
                        className={`upload ${dragTarget === "ticket" ? "drag-active" : ""}`}
                        onDragOver={(e) => handleDragOver(e, "ticket")}
                        onDragLeave={handleDragLeave}
                        onDrop={(e) => {
                          e.preventDefault();
                          setDragTarget(null);

                          handleSelfBookingFileChange(
                            {
                              target: {
                                files: e.dataTransfer.files,
                              },
                            },
                            'ticket'
                          );
                        }}
                      >
                      <label htmlFor='selfBookedTicketBill' className="upload-button">
                        <span className="required-label">
                          Ticket Details <span className="required-star">*</span>:
                        </span>
                        {!selfTicketBillUrl && <FaCloudUploadAlt size={20} />}
                        <span>
                          {selfTicketBillUrl ? (
                            <span className='view-download'>
                              <a href={selfTicketBillUrl} target="_blank" rel="noopener noreferrer" 
                                onClick={(e) =>
                                  handleSecureLinkOpen(
                                    e,
                                    typeof formData.selfBookedTicketBill === 'string'
                                      ? formData.selfBookedTicketBill
                                      : selfTicketBillUrl,
                                    selfTicketBillName || getCleanFileName(selfTicketBillUrl)
                                  )
                                }
                                style={{ color: "#224D3D", fontWeight: "600" }}>
                                {selfTicketBillName || getCleanFileName(selfTicketBillUrl)}
                              </a>
                            </span>
                          ) : <p className='upload-icon'>Upload</p>}
                        </span>
                        <input
                          type="file"
                          id="selfBookedTicketBill"
                          name="selfBookedTicketBill"
                          accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                          onChange={(e) => handleSelfBookingFileChange(e, 'ticket')}
                        />
                        <small className="upload-size-hint">Maximum file size: 10 MB</small>
                      </label>
                      {selfTicketBillUrl &&
                        <span className='delete-upload'>
                          <MdDelete onClick={() => handleSelfBookingFileDelete('ticket')} />
                        </span>
                      }
                    </div>
                    {errors.selfBookedTicketBill && (
                      <p className="error-text">{REQUEST_MESSAGES.TICKET_BILL_REQUIRED}</p>
                    )}
                  </>
                )}

                {((formData.supportNeeded==='Accommodation') || (formData.supportNeeded==='Travel + Accommodation')) && (
                  <>
                    <label>
                      Accommodation Amount:
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        name="selfBookedHotelAmount"
                        value={formData.selfBookedHotelAmount}
                        onChange={handleInputChange}
                        className={errors.selfBookedHotelAmount ? "error-field" : ""}
                        required
                      />
                    </label>

                    <div className='upload'>
                      <label htmlFor='selfBookedHotelBill' className="upload-button">
                        <span className="required-label">
                          Accommodation Bill <span className="required-star">*</span>:
                        </span>
                        {!selfHotelBillUrl && <FaCloudUploadAlt size={20} />}
                        <span>
                          {selfHotelBillUrl ? (
                            <span className='view-download'>
                              <a href={selfHotelBillUrl} target="_blank" rel="noopener noreferrer"
                                onClick={(e) =>
                                  handleSecureLinkOpen(
                                    e,
                                    typeof formData.selfBookedHotelBill === 'string'
                                      ? formData.selfBookedHotelBill
                                      : selfHotelBillUrl,
                                    selfHotelBillName || getCleanFileName(selfHotelBillUrl)
                                  )
                                }
                                style={{ color: "#224D3D", fontWeight: "600" }}>
                                {selfHotelBillName || getCleanFileName(selfHotelBillUrl)}
                              </a>
                            </span>
                          ) : <p className='upload-icon'>Upload</p>}
                        </span>
                        <input
                          type="file"
                          id="selfBookedHotelBill"
                          name="selfBookedHotelBill"
                          accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                          onChange={(e) => handleSelfBookingFileChange(e, 'hotel')}
                        />
                        <small className="upload-size-hint">Maximum file size: 10 MB</small>
                      </label>
                      {selfHotelBillUrl &&
                        <span className='delete-upload'>
                          <MdDelete onClick={() => handleSelfBookingFileDelete('hotel')} />
                        </span>
                      }
                    </div>
                    {errors.selfBookedHotelBill && (
                      <p className="error-text">{REQUEST_MESSAGES.ACCOMMODATION_BILL_REQUIRED}</p>
                    )}
                  </>
                )}

<label>
                  Payment Mode:
                  <select
                    name="paymentMode"
                    value={formData.paymentMode}
                    onChange={handleInputChange}
                  >
                    <option value="">Select Payment Mode</option>
                    <option value="Cash">Cash</option>
                    <option value="UPI">UPI</option>
                    <option value="Card">Card</option>
                    <option value="Other">Other</option>
                  </select>
                </label>
                {formData.paymentMode && (
                  <>
                    {formData.paymentMode === "Other" && (
                      <label>
                        Specify Payment Method:
                        <input
                          type="text"
                          name="otherPaymentMode"
                          value={formData.otherPaymentMode || ""}
                          onChange={handleInputChange}
                          placeholder="Enter payment method"
                          required={formData.paymentMode === "Other"}
                          className={errors.otherPaymentMode ? "error-field" : ""}
                        />
                      </label>
                    )}

                    {formData.paymentMode !== "Cash" && (
                      <label>
                        Payment Reference (UTR/Txn ID/Invoice Number):
                        <input
                          type="text"
                          name="paymentReference"
                          value={formData.paymentReference}
                          onChange={handleInputChange}
                          placeholder="Enter payment reference"
                          className={errors.paymentReference ? "error-field" : ""}
                          required={
                            formData.selfBooked &&
                            formData.paymentMode &&
                            formData.paymentMode !== "Cash"
                          }
                        />
                      </label>
                    )}

                    <div
                      className={`upload ${
                        dragTarget === "payment" ? "drag-active" : ""
                      }`}
                      onDragOver={(e) => handleDragOver(e, "payment")}
                      onDragLeave={handleDragLeave}
                      onDrop={(e) => {
                        e.preventDefault();
                        setDragTarget(null);

                        handlePaymentProofChange({
                          target: {
                            files: e.dataTransfer.files,
                          },
                        });
                      }}
                    >
                      <label htmlFor="paymentProof" className="upload-button">
                        <span className="required-label">
                          Payment Proof{" "}
                          <span className="required-star">*</span>:
                        </span>

                        {!selfPaymentProofUrl && (
                          <FaCloudUploadAlt size={20} />
                        )}

                        <span>
                          {selfPaymentProofUrl ? (
                            <span className="view-download">
                              <a
                                href={selfPaymentProofUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={(e) =>
                                  handleSecureLinkOpen(
                                    e,
                                    typeof formData.paymentProof === "string"
                                      ? formData.paymentProof
                                      : selfPaymentProofUrl,
                                    selfPaymentProofName ||
                                      getCleanFileName(selfPaymentProofUrl)
                                  )
                                }
                                style={{
                                  color: "#224D3D",
                                  fontWeight: "600",
                                }}
                              >
                                {selfPaymentProofName ||
                                  getCleanFileName(selfPaymentProofUrl)}
                              </a>
                            </span>
                          ) : (
                            <p className="upload-icon">Upload</p>
                          )}
                        </span>

                        <input
                          type="file"
                          id="paymentProof"
                          name="paymentProof"
                          accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                          onChange={handlePaymentProofChange}
                        />
                        <small className="upload-size-hint">Maximum file size: 10 MB</small>
                      </label>

                      {selfPaymentProofUrl && (
                        <span className="delete-upload">
                          <MdDelete onClick={handlePaymentProofDelete} />
                        </span>
                      )}
                    </div>

                    {errors.paymentProof && (
                      <p className="error-text">PAYMENT_PROOF_REQUIRED</p>
                    )}
                  </>
                )}
                <label>
                  Paid Date:
                  <input
                    type="date"
                    name="paymentDate"
                    value={formData.paymentDate}
                    onChange={handleInputChange}
                    max={getTodayInputDate()}
                    className={errors.paymentDate ? "error-field" : ""}
                    required={formData.selfBooked}
                  />
                </label>

                <label>
                  Reimbursement Payment Type:
                  <select
                    name="reimbursementPaymentType"
                    value={formData.reimbursementPaymentType}
                    onChange={handleInputChange}
                  >
                    <option value="">Select Payment Type</option>
                    <option value="UPI">UPI</option>
                    <option value="Account Details">Account Details</option>
                  </select>
                </label>

                {formData.reimbursementPaymentType && (
                  <>
                    {formData.reimbursementPaymentType === "UPI" && (
                      <>
                        <label>
                          UPI ID:
                          <input
                            type="text"
                            name="reimbursementUpiId"
                            value={formData.reimbursementUpiId}
                            onChange={handleInputChange}
                          />
                        </label>
                        <label>
                          UPI Number:
                          <input
                            type="text"
                            name="reimbursementUpiNumber"
                            value={formData.reimbursementUpiNumber}
                            onChange={handleInputChange}
                          />
                        </label>
                      </>
                    )}

                    {formData.reimbursementPaymentType === "Account Details" && (
                      <>
                        <label>
                          Account Number:
                          <input
                            type="text"
                            name="reimbursementAccountNumber"
                            value={formData.reimbursementAccountNumber}
                            onChange={handleInputChange}
                          />
                        </label>
                        <label>
                          Account Holder Name:
                          <input
                            type="text"
                            name="reimbursementAccountHolderName"
                            value={formData.reimbursementAccountHolderName}
                            onChange={handleInputChange}
                          />
                        </label>
                      </>
                    )}

                    <div
                      className={`upload ${
                        dragTarget === "reimbursementSupportingDocument"
                          ? "drag-active"
                          : ""
                      }`}
                      onDragOver={(event) =>
                        handleDragOver(event, "reimbursementSupportingDocument")
                      }
                      onDragLeave={handleDragLeave}
                      onDrop={(event) => {
                        event.preventDefault();
                        setDragTarget(null);
                        handleReimbursementSupportingDocumentChange({
                          target: { files: event.dataTransfer.files },
                        });
                      }}
                    >
                      <label
                        htmlFor="reimbursementSupportingDocument"
                        className="upload-button"
                      >
                        <span className="required-label">
                          Supporting Document:
                        </span>
                        {!(selfReimbursementDocFile || selfReimbursementDocUrl) && (
                          <FaCloudUploadAlt size={20} />
                        )}
                        <span>
                          {(selfReimbursementDocFile || selfReimbursementDocUrl) ? (
                            <span className="view-download">
                              {selfReimbursementDocName ||
                                getCleanFileName(selfReimbursementDocUrl)}
                            </span>
                          ) : (
                            <p className="upload-icon">Upload</p>
                          )}
                        </span>
                        <input
                          type="file"
                          id="reimbursementSupportingDocument"
                          name="reimbursementSupportingDocument"
                          accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                          onChange={handleReimbursementSupportingDocumentChange}
                        />
                        <small className="upload-size-hint">Maximum file size: 10 MB</small>
                      </label>
                      {(selfReimbursementDocFile || selfReimbursementDocUrl) && (
                        <span className="delete-upload">
                          <MdDelete
                            onClick={handleReimbursementSupportingDocumentDelete}
                          />
                        </span>
                      )}
                    </div>
                  </>
                )}
              </>
            )}
          </fieldset>
          </>}

          {renderFieldsBasedOnSupport()}

          {TRAVEL_SUPPORT_TYPES.includes(formData.supportNeeded) && (
            <div className="declaration-box final-declaration-box">
              <label className="declaration-label">
                <input
                  type="checkbox"
                  checked={formData.finalDeclarationAccepted}
                  onChange={(e) =>
                    handleDeclarationChange('finalDeclarationAccepted', e.target.checked)
                  }
                />
                <span>{getFinalDeclarationText(formData.selfBooked)}</span>
              </label>
            </div>
          )}

          {showSavedAddressPopup && (
            <div
              className="saved-address-overlay"
              onClick={closeSavedAddressPopup}
            >
              <div
                className="saved-address-modal"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="saved-address-header">
                  <h2>Saved Addresses</h2>

                  <button
                    type="button"
                    className="saved-address-close"
                    onClick={closeSavedAddressPopup}
                  >
                    ✕
                  </button>
                </div>

                <div className="saved-address-search-wrapper">
                  <input
                    type="text"
                    placeholder="Search saved addresses..."
                    value={savedAddressSearch}
                    onChange={(e) => setSavedAddressSearch(e.target.value)}
                    className="saved-address-search"
                  />
                </div>

                <div className="saved-address-list">

                  {loadingSavedAddresses ? (

                    <div className="saved-address-empty">
                      Loading saved addresses...
                    </div>

                  ) : savedAddresses.length === 0 ? (

                    <div className="saved-address-empty">
                      No saved addresses found.
                    </div>

                  ) : (

                    savedAddresses
                      .filter((item) =>
                        item.address
                          .toLowerCase()
                          .includes(savedAddressSearch.toLowerCase())
                      )
                      .map((item) => (
                        <div
                          key={item.id}
                          className="saved-address-item"
                          onClick={() => handleSavedAddressSelect(item.address)}
                        >
                          <div className="saved-address-pin">
                            📍
                          </div>

                          <div className="saved-address-content">
                            <div className="saved-address-text">
                              {item.address}
                            </div>
                          </div>
                        </div>
                      ))

                  )}

                </div>
              </div>
            </div>
          )}

<div>
  <button
    type="submit"
    className="submit-button"
    onClick={(e) => {
      const declarationUnchecked = hasUncheckedDeclarations();

      if (declarationUnchecked) {
        e.preventDefault();
        notifyError(
          REQUEST_MESSAGES.DECLARATION_BEFORE_SUBMIT,
          "bottom-center"
        );
        return;
      }

      if (!isFormValid) {
        const missing = findEmptyFields();
        const firstKey = Object.keys(missing)[0];

        if (firstKey) {
          const el = document.querySelector(`[name="${firstKey}"]`);

          if (el && typeof el.scrollIntoView === "function") {
            el.scrollIntoView({
              behavior: "smooth",
              block: "center",
            });
          }
        }

        setErrors(missing);
        notifyError(
          REQUEST_MESSAGES.MANDATORY_FIELDS_REQUIRED,
          "bottom-center"
        );
        return;
      }

      setErrors({});
    }}
  >
    Submit
  </button>
</div>

        </form>
 </div>
  </div>
      </main>
      <FilePreviewModal
        isOpen={showDocPreviewModal}
        onClose={() => setShowDocPreviewModal(false)}
        onDownload={async () => {
          try {
            await openSecureFileUrl(docDownloadFile, token, true, false);
          } finally {
            setShowDocPreviewModal(false);
          }
        }}
      />
      <Footer />
    </div>
  );
}

export default Request;